import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { legacyApi, WorkflowEntityDefinition, WorkflowEventDefinition, WorkflowHistoryEntry } from '../../services/legacyApi';
import { BOM, Item, MaterialUomConversion, WorkOrder, WorkOrderDailyEntriesListResponse, WorkOrderDailyEntryRequest, WorkOrderItemMetadataUpdate, WorkOrderMaterialsResponse } from '../../services/types';
import { ArrowLeft, Play, CheckCircle, Trash2, Plus, PanelRightOpen, PanelRightClose, Pencil, Eye, RotateCcw, Printer } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { CompactTable } from '../../components/ui/CompactTable';
import { Button } from '../../components/ui/button';
import { useConfirm } from '../../hooks/use-confirm';
import { toast } from 'sonner';
import { CompletionModal } from './CompletionModal';
import { TableFilterBar } from '../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../hooks/useTableFilterState';
import { FlowCoachPanel } from '../../components/workflow/FlowCoachPanel';
import { FlowEntryBanner } from '../../components/workflow/FlowEntryBanner';
import { WorkflowRightPanel } from '../../components/workflow/WorkflowRightPanel';
import { StatusGuidePanel } from '../../modules/legacy-core/components/StatusGuidePanel';
import { StatusHistoryPanel } from '../../modules/legacy-core/components/StatusHistoryPanel';
import { useSettings } from '../../context/SettingsContext';
import { getOperatorWorkOrderLabel, getWorkOrderDescriptionSummary } from './workOrderDisplay';
import EntityTypeaheadPicker, { type PickerOption } from '../../components/search/EntityTypeaheadPicker';

type IssueMaterialOption = {
    key: string;
    item_id: number;
    material_row_id?: number;
    label: string;
    row_type: 'PLANNED' | 'UNPLANNED';
    item_code: string;
    item_name: string;
    required_qty: number;
    issued_qty: number;
    remaining_qty: number;
    available_qty: number;
    shortage_qty: number;
    status: string;
    base_unit_id?: number | null;
    base_unit_name?: string | null;
    wo_item_id?: number | null;
    source_type?: string;
    line_label?: string | null;
    remarks?: string;
    uom_conversions?: MaterialUomConversion[];
};

type IssueUomOption = {
    unitId: string;
    label: string;
    factorToBase: number;
    isBase: boolean;
};

type DirectCostType = 'SUBCON' | 'TRANSPORT' | 'SERVICE' | 'DIRECT_LABOUR_HIRE' | 'OTHER';

const DIRECT_COST_OPTIONS: Array<{ value: DirectCostType; label: string }> = [
    { value: 'SUBCON', label: 'Subcontracting' },
    { value: 'TRANSPORT', label: 'Transportation' },
    { value: 'SERVICE', label: 'Service' },
    { value: 'DIRECT_LABOUR_HIRE', label: 'Direct Labour Hire' },
    { value: 'OTHER', label: 'Other' },
];

const DIRECT_COST_TYPES = new Set<DirectCostType>(DIRECT_COST_OPTIONS.map((option) => option.value));

type DirectCostDraft = {
    key: string;
    cost_type: DirectCostType;
    amount: string;
    remarks: string;
};

type WorkOrderPartRow = NonNullable<WorkOrderMaterialsResponse['parts']>[number];
type FlattenedWorkOrderPartRow = WorkOrderPartRow & { depth: number };

const formatDecimal = (
    value: number | string | null | undefined,
    maximumFractionDigits = 3,
    minimumFractionDigits = 0,
): string => {
    const numeric = Number(value ?? 0);
    if (!Number.isFinite(numeric)) return '0';
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
    }).format(numeric);
};

const formatQty = (value: number | string | null | undefined): string => formatDecimal(value, 3, 0);
const formatWeight = (value: number | string | null | undefined): string => formatDecimal(value, 3, 0);
const formatHours = (value: number | string | null | undefined): string => formatDecimal(value, 2, 0);
const formatMoney = (value: number | string | null | undefined): string => formatDecimal(value, 2, 2);
const WORK_ORDER_LEVEL_ENTRY_LABEL = 'Work Order-level entry';

const flattenPartTree = (parts: WorkOrderPartRow[] | undefined, depth = 0): FlattenedWorkOrderPartRow[] =>
    (parts || []).flatMap((part) => {
        const current: FlattenedWorkOrderPartRow = { ...part, depth };
        const children = flattenPartTree((part.children || []) as WorkOrderPartRow[], depth + 1);
        return [current, ...children];
    });

const resolveChildUiState = (
    part: FlattenedWorkOrderPartRow,
    workOrderItemStateMap: Map<number, NonNullable<WorkOrder['items']>[number]>,
) => {
    const itemState = workOrderItemStateMap.get(Number(part.wo_item_id));
    const editable = typeof part.editable === 'boolean' ? part.editable : !!itemState?.editable;
    const deletable = typeof part.deletable === 'boolean' ? part.deletable : !!itemState?.deletable;
    const deleteLockReason =
        part.delete_lock_reason
        || itemState?.delete_lock_reason
        || (part.has_postings || itemState?.has_postings ? 'Used in production postings.' : 'Delete is available only for unused child items.');
    return {
        editable,
        deletable,
        deleteLockReason,
    };
};

export const WorkOrderDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const woId = Number(id);
    const { getSetting } = useSettings();
    const [wo, setWO] = useState<WorkOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<Item[]>([]);
    const [boms, setBoms] = useState<BOM[]>([]);
    const [materialsPayload, setMaterialsPayload] = useState<WorkOrderMaterialsResponse | null>(null);
    const [dailyPayload, setDailyPayload] = useState<WorkOrderDailyEntriesListResponse | null>(null);

    const [unplannedOpen, setUnplannedOpen] = useState(false);
    const [unplannedItemId, setUnplannedItemId] = useState('');
    const [unplannedDefaultQty, setUnplannedDefaultQty] = useState('0');
    const [unplannedRemarks, setUnplannedRemarks] = useState('');

    const [entryDate, setEntryDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [selectedWoItemId, setSelectedWoItemId] = useState('');
    const [outputQty, setOutputQty] = useState('0');
    const [outputWeightKgs, setOutputWeightKgs] = useState('0');
    const [autoBackflushBom, setAutoBackflushBom] = useState(false);
    const [includeChildRecipeMaterials, setIncludeChildRecipeMaterials] = useState(false);
    const [laborHours, setLaborHours] = useState('0');
    const [hourlyRate, setHourlyRate] = useState('0');
    const [laborOverride, setLaborOverride] = useState('');
    const [installationLaborHours, setInstallationLaborHours] = useState('0');
    const [installationHourlyRate, setInstallationHourlyRate] = useState('0');
    const [installationLaborOverride, setInstallationLaborOverride] = useState('');
    const [overheadCost, setOverheadCost] = useState('0');
    const [entryRemarks, setEntryRemarks] = useState('');
    const [issueLines, setIssueLines] = useState<Array<{
        key: string;
        option_key?: string;
        item_id: string;
        qty: string;
        transaction_unit_id?: string;
        row_type: 'PLANNED' | 'UNPLANNED';
        remarks: string;
        material_row_id?: number;
        wo_item_id?: number | null;
        source_type?: string;
    }>>([{ key: 'line-1', option_key: '', item_id: '', qty: '0', transaction_unit_id: '', row_type: 'PLANNED', remarks: '', material_row_id: undefined }]);
    const [quickIssueQtyByRow, setQuickIssueQtyByRow] = useState<Record<string, string>>({});
    const [quickIssueRemarksByRow, setQuickIssueRemarksByRow] = useState<Record<string, string>>({});
    const [materialSourceFilter, setMaterialSourceFilter] = useState<'ALL' | 'ESTIMATE' | 'RECIPE' | 'MANUAL'>('ALL');
    const [materialLineFilter, setMaterialLineFilter] = useState('ALL');
    const [directCostLines, setDirectCostLines] = useState<DirectCostDraft[]>([
        { key: 'direct-1', cost_type: 'SUBCON', amount: '0', remarks: '' },
    ]);
    const [subPartOpen, setSubPartOpen] = useState(false);
    const [editingSubPartId, setEditingSubPartId] = useState<number | null>(null);
    const [subPartParentId, setSubPartParentId] = useState('');
    const [subPartDescription, setSubPartDescription] = useState('');
    const [subPartQty, setSubPartQty] = useState('0');
    const [subPartUom, setSubPartUom] = useState('Nos');
    const [subPartDimensions, setSubPartDimensions] = useState('');
    const [subPartThickness, setSubPartThickness] = useState('');
    const [subPartColor, setSubPartColor] = useState('');
    const [subPartFinish, setSubPartFinish] = useState('');
    const [subPartLineRemarks, setSubPartLineRemarks] = useState('');
    const [subPartCompletionDate, setSubPartCompletionDate] = useState('');
    const [lineDetailsDraft, setLineDetailsDraft] = useState<WorkOrderItemMetadataUpdate>({
        dimensions: '',
        thickness: '',
        color: '',
        finish: '',
        line_remarks: '',
        planned_completion_date: '',
    });
    const [editingLineDetailsId, setEditingLineDetailsId] = useState<number | null>(null);
    const [entryBusy, setEntryBusy] = useState(false);
    const [entryActionBusyId, setEntryActionBusyId] = useState<number | null>(null);
    const [childActionBusyId, setChildActionBusyId] = useState<number | null>(null);
    const [recipeBusyLineId, setRecipeBusyLineId] = useState<number | null>(null);
    const [recipeSelectionByLine, setRecipeSelectionByLine] = useState<Record<number, string>>({});
    const [recipeCreateChildrenByLine, setRecipeCreateChildrenByLine] = useState<Record<number, boolean>>({});
    const [selectedEntryId, setSelectedEntryId] = useState<number | null>(null);
    const dailyEntryRef = useRef<HTMLDivElement | null>(null);

    const { confirm } = useConfirm();
    const [completionOpen, setCompletionOpen] = useState(false);
    const [actionBusy, setActionBusy] = useState<'start' | 'complete' | 'delete' | null>(null);
    const [workflowPanelOpen, setWorkflowPanelOpen] = useState(false);
    const { state, setFilter, isDirty } = useTableFilterState({ q_materials: '', q_entries: '' });
    const globalSimpleModeEnabled = useMemo(() => {
        const raw = getSetting('sales_simple_mode_enabled');
        return ['1', 'true', 'yes', 'on', 'enabled'].includes(String(raw || '').trim().toLowerCase());
    }, [getSetting]);
    const simpleModeEnabled = useMemo(() => {
        const resolved = String(wo?.resolved_flow_mode || wo?.flow_mode || '').trim().toUpperCase();
        if (resolved === 'SIMPLE') return true;
        if (resolved === 'PRODUCTION') return false;
        return globalSimpleModeEnabled;
    }, [wo?.resolved_flow_mode, wo?.flow_mode, globalSimpleModeEnabled]);
    const flowModeLabel = simpleModeEnabled ? 'Simple' : 'Production';
    const normalizeItemType = (value?: string | null): string => String(value || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
    const isFinishedGood = (item: Item): boolean => String(item.item_role || '').toUpperCase() === 'FG';
    const isStockMaterial = (item: Item): boolean => {
        const role = String(item.item_role || '').toUpperCase();
        const type = normalizeItemType(item.item_type);
        return type === 'stock' && !['FG', 'SERVICE', 'CONSUMABLE', 'NON_STOCK', 'NONSTOCK', 'ASSET', 'FIXED_ASSET'].includes(role);
    };
    const unplannedMaterialOptions = useMemo<PickerOption<string>[]>(() => {
        return items
            .filter((item) => isStockMaterial(item) && !isFinishedGood(item))
            .map((item) => ({
                value: String(item.id),
                label: `${item.code} - ${item.name}`,
                description: item.category || 'Material',
                badges: [
                    { label: item.uom || '-', tone: 'slate' as const },
                    ...(item.current_stock !== undefined ? [{ label: `Stock ${formatQty(item.current_stock)}`, tone: Number(item.current_stock || 0) > 0 ? 'green' as const : 'amber' as const }] : []),
                ],
            }));
    }, [items]);

    // Workflow State
    const [workflowDefinition, setWorkflowDefinition] = useState<WorkflowEntityDefinition | null>(null);
    const [nextActions, setNextActions] = useState<WorkflowEventDefinition[]>([]);
    const [history, setHistory] = useState<WorkflowHistoryEntry[]>([]);

    const emptyIssueLine = () => ({
        key: `${Date.now()}-${Math.random()}`,
        option_key: '',
        item_id: '',
        qty: '0',
        transaction_unit_id: '',
        row_type: 'PLANNED' as 'PLANNED' | 'UNPLANNED',
        remarks: '',
        material_row_id: undefined as number | undefined,
        wo_item_id: undefined as number | undefined,
        source_type: undefined as string | undefined,
    });

    const getMaterialRowKey = (row: any) => `${row.wo_id || woId}:${String(row.row_id)}`;
    const formatMaterialSourceLabel = (row: any) => {
        const source = String(row.source_type || '').toUpperCase();
        if (source === 'RECIPE') return 'Recipe';
        if (source === 'MANUAL' || row.row_type === 'UNPLANNED') return 'Manual';
        if (source === 'SUMMARY') return 'Summary';
        return 'Estimate';
    };

    const requirementsColumns = [
        {
            header: "Material",
            accessor: (row: any) => (
                <div className="min-w-[260px]">
                    <div className="font-medium">{row.item_code} - {row.item_name}</div>
                    {(row.base_unit_name || inventoryItemMap.get(Number(row.item_id || 0))?.uom) ? (
                        <div className="text-xs text-slate-500">Base UOM: {row.base_unit_name || inventoryItemMap.get(Number(row.item_id || 0))?.uom}</div>
                    ) : null}
                    {row.row_type === 'PLANNED' && row.remarks ? (
                        <div className="text-xs text-slate-500">
                            Calc: {row.remarks}
                        </div>
                    ) : null}
                    {row.line_label ? (
                        <div className="text-xs text-slate-500">Line: {row.line_label}</div>
                    ) : null}
                </div>
            ),
            className: 'font-medium min-w-[220px]'
        },
        {
            header: "Source",
            accessor: (row: any) => (
                <span className={
                    row.row_type === 'UNPLANNED'
                        ? "text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium"
                        : String(row.source_type || '').toUpperCase() === 'RECIPE'
                            ? "text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium"
                        : "text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 font-medium"
                }>
                    {formatMaterialSourceLabel(row)}
                </span>
            ),
            className: 'text-center min-w-[90px]'
        },
        {
            header: "Requirement / WO Balance",
            accessor: (row: any) => (
                <div className="min-w-[170px] space-y-0.5 text-xs text-slate-600">
                    <div className="flex justify-between gap-3">
                        <span>Required</span>
                        <span className="font-medium text-slate-900">{formatQty(row.required_qty || row.target_qty || 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                        <span>Issued to WO</span>
                        <span className="font-medium text-slate-900">{formatQty(row.issued_to_wo_qty ?? row.issued_qty ?? 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                        <span>Consumed</span>
                        <span className="font-medium text-slate-900">{formatQty(row.consumed_qty || 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                        <span>WO Balance</span>
                        <span className={Number(row.wo_balance_qty || 0) > 0 ? "font-semibold text-blue-700" : "font-medium text-slate-900"}>{formatQty(row.wo_balance_qty || 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                        <span>To Issue</span>
                        <span className="font-medium text-slate-900">{formatQty(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0)}</span>
                    </div>
                </div>
            ),
            className: 'min-w-[170px]',
        },
        {
            header: "Stock",
            accessor: (row: any) => (
                <div className="min-w-[150px] space-y-1 text-xs text-slate-600">
                    <div className="flex justify-between gap-3">
                        <span>Available</span>
                        <span className="font-medium text-slate-900">{formatQty(row.available_qty || 0)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                        <span>Shortage</span>
                        <span className={Number(row.shortage_qty || 0) > 0 ? "font-semibold text-red-600" : "font-medium text-slate-500"}>
                            {formatQty(row.shortage_qty || 0)}
                        </span>
                    </div>
                    <span className={
                        row.status === 'SHORT'
                            ? "inline-flex text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium"
                            : row.status === 'ISSUED' || row.status === 'ISSUED_TO_WO'
                                ? "inline-flex text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700 font-medium"
                                : row.status === 'CONSUMED'
                                    ? "inline-flex text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium"
                                : "inline-flex text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium"
                    }>
                        {row.status === 'SHORT' ? 'Insufficient' : row.status === 'ISSUED_TO_WO' ? 'At WO' : row.status === 'CONSUMED' ? 'Consumed' : row.status === 'ISSUED' ? 'Issued' : 'Ready'}
                    </span>
                </div>
            ),
            className: 'min-w-[150px]',
        },
        {
            header: "Issue / Return",
            accessor: (row: any) => {
                const key = getMaterialRowKey(row);
                const toIssue = Number(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0);
                const balance = Number(row.wo_balance_qty || 0);
                const qty = quickIssueQtyByRow[key] ?? (toIssue > 0 ? String(toIssue) : balance > 0 ? String(balance) : '1');
                return (
                    <div className="flex min-w-[190px] items-center justify-end gap-1">
                        <input
                            type="number"
                            min="0"
                            step="0.001"
                            className="w-20 rounded border px-2 py-1 text-xs"
                            value={qty}
                            onChange={(event) => setQuickIssueQtyByRow((prev) => ({ ...prev, [key]: event.target.value }))}
                        />
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={entryBusy || simpleModeEnabled}
                            onClick={() => handleQuickIssueRow(row)}
                        >
                            Issue
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={entryBusy || simpleModeEnabled || balance <= 0}
                            onClick={() => handleReturnMaterialRow(row)}
                        >
                            Return
                        </Button>
                    </div>
                );
            },
            className: 'min-w-[190px] text-right',
        },
    ];

    const renderSectionTitle = (title: string, description?: string) => (
        <div className="border-b pb-2">
            <div className="text-sm font-semibold text-slate-900">{title}</div>
            {description ? <div className="text-xs text-slate-500">{description}</div> : null}
        </div>
    );

    const renderSubPartForm = () => (
        <form onSubmit={handleCreateSubPart} className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
                <label className="text-sm font-medium">Parent Part</label>
                <select className="w-full border rounded p-2 mt-1" value={subPartParentId} onChange={(event) => setSubPartParentId(event.target.value)} disabled={editingSubPartId !== null}>
                    <option value="">Select parent part...</option>
                    {parentPartOptions.map((part) => (
                        <option key={part.wo_item_id} value={part.wo_item_id}>{part.description}</option>
                    ))}
                </select>
            </div>
            <div>
                <label className="text-sm font-medium">Sub-part No. (Auto)</label>
                <div className="w-full border rounded px-3 py-2 mt-1 text-sm bg-white text-slate-700 min-h-[42px] flex items-center">
                    {generatedSubPartPreview || 'Select parent part to generate sub-part number'}
                </div>
            </div>
            <div>
                <label className="text-sm font-medium">Description</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartDescription} onChange={(event) => setSubPartDescription(event.target.value)} />
            </div>
            <div>
                <label className="text-sm font-medium">Target Qty</label>
                <input type="number" min="0" step="0.001" className="w-full border rounded p-2 mt-1" value={subPartQty} onChange={(event) => setSubPartQty(event.target.value)} />
            </div>
            <div>
                <label className="text-sm font-medium">Dimensions</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartDimensions} onChange={(event) => setSubPartDimensions(event.target.value)} placeholder="L x W x H" />
            </div>
            <div>
                <label className="text-sm font-medium">Thickness</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartThickness} onChange={(event) => setSubPartThickness(event.target.value)} />
            </div>
            <div>
                <label className="text-sm font-medium">Color</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartColor} onChange={(event) => setSubPartColor(event.target.value)} />
            </div>
            <div>
                <label className="text-sm font-medium">Finish</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartFinish} onChange={(event) => setSubPartFinish(event.target.value)} />
            </div>
            <div>
                <label className="text-sm font-medium">Completion Date</label>
                <input type="date" className="w-full border rounded p-2 mt-1" value={subPartCompletionDate} onChange={(event) => setSubPartCompletionDate(event.target.value)} />
            </div>
            <div className="md:col-span-4">
                <label className="text-sm font-medium">Line Remarks</label>
                <input className="w-full border rounded p-2 mt-1" value={subPartLineRemarks} onChange={(event) => setSubPartLineRemarks(event.target.value)} />
            </div>
            <div className="md:col-span-6 flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetSubPartForm} disabled={entryBusy}>Cancel</Button>
                <Button type="submit" disabled={entryBusy}>{editingSubPartId ? 'Save Sub-part' : 'Create Sub-part'}</Button>
            </div>
        </form>
    );

    const dailyColumns = [
        { header: "Date", accessor: (entry: any) => entry.entry_date, className: '' },
        {
            header: "Line",
            accessor: (entry: any) => (
                <div className="min-w-[140px]">
                    <div className="font-medium text-slate-800">{entry.wo_item_label || WORK_ORDER_LEVEL_ENTRY_LABEL}</div>
                    {entry.effective_state && entry.effective_state !== 'ACTIVE' ? (
                        <div className="text-[11px] text-slate-500">{entry.effective_state}</div>
                    ) : null}
                </div>
            ),
        },
        { header: "Output", accessor: (entry: any) => formatQty(entry.output_qty || 0), className: 'text-right' },
        { header: "Weight (kg)", accessor: (entry: any) => formatWeight(entry.output_weight_kgs || 0), className: 'text-right' },
        { header: "Prod Hrs", accessor: (entry: any) => formatHours(entry.labor_hours || 0), className: 'text-right' },
        { header: "Install Hrs", accessor: (entry: any) => formatHours(entry.installation_labor_hours || 0), className: 'text-right' },
        { header: "Issued Lines", accessor: (entry: any) => Number((entry.issue_lines || []).length), className: 'text-right' },
        { header: "Cost", accessor: (entry: any) => formatMoney(entry.total_entry_cost || 0), className: 'text-right' },
        {
            header: "Warnings",
            accessor: (entry: any) => ((entry.warnings || []).length ? `${(entry.warnings || []).length} warning(s)` : '-')
        },
        {
            header: "Actions",
            accessor: (entry: any) => {
                const effectiveState = String(entry.effective_state || 'ACTIVE').toUpperCase();
                return (
                    <div className="flex items-center justify-end gap-1 min-w-[220px]">
                        <Button type="button" size="sm" variant="ghost" onClick={() => setSelectedEntryId(Number(entry.entry_id))}>
                            <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => hydrateEntryForm(entry)}
                            disabled={effectiveState === 'REVERSED'}
                        >
                            <RotateCcw className="w-4 h-4 mr-1" /> Replace
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleReverseDailyEntry(Number(entry.entry_id))}
                            disabled={effectiveState !== 'ACTIVE' || entryActionBusyId === Number(entry.entry_id) || simpleModeEnabled}
                        >
                            Reverse
                        </Button>
                    </div>
                );
            },
            className: 'text-right'
        },
    ];

    const filteredMaterials = useMemo(() => {
        const query = state.q_materials.trim().toLowerCase();
        const useSummary = materialSourceFilter === 'ALL' && materialLineFilter === 'ALL';
        const source = useSummary ? (materialsPayload?.material_summary || materialsPayload?.materials || []) : (materialsPayload?.materials || []);
        const filteredByPills = source.filter((row: any) => {
            const sourceType = String(row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE')).toUpperCase();
            const lineMatch = materialLineFilter === 'ALL' || String(row.wo_item_id || '') === materialLineFilter;
            const sourceMatch = materialSourceFilter === 'ALL' || sourceType === materialSourceFilter || (materialSourceFilter === 'MANUAL' && row.row_type === 'UNPLANNED');
            return lineMatch && sourceMatch;
        });
        if (!query) return filteredByPills;
        return filteredByPills.filter((row: any) =>
            `${row.item_code} ${row.item_name} ${row.row_type} ${row.source_type || ''} ${row.line_label || ''} ${row.remarks || ''}`.toLowerCase().includes(query)
        );
    }, [materialLineFilter, materialSourceFilter, materialsPayload?.material_summary, materialsPayload?.materials, state.q_materials]);

    const filteredEntries = useMemo(() => {
        const query = state.q_entries.trim().toLowerCase();
        const source = dailyPayload?.entries || [];
        if (!query) return source;
        return source.filter((entry: any) =>
            `${entry.entry_date} ${entry.remarks || ''} ${(entry.warnings || []).join(' ')}`.toLowerCase().includes(query)
        );
    }, [dailyPayload?.entries, state.q_entries]);

    const issueMaterialOptions = useMemo<IssueMaterialOption[]>(() => {
        const existingRows = (materialsPayload?.materials || [])
            .filter((row) => Number(row.wo_balance_qty || 0) > 0)
            .filter((row) => row.item_id !== null)
            .map((row) => ({
                key: `${row.row_type}:${String(row.row_id)}`,
                item_id: Number(row.item_id),
                material_row_id: row.row_type === 'UNPLANNED' && typeof row.row_id === 'number' ? Number(row.row_id) : undefined,
                label: `${row.item_code} - ${row.item_name}${row.line_label ? ` (${row.line_label})` : ''}`,
                row_type: row.row_type === 'UNPLANNED' ? 'UNPLANNED' : 'PLANNED' as 'PLANNED' | 'UNPLANNED',
                item_code: String(row.item_code || ''),
                item_name: String(row.item_name || ''),
                required_qty: Number(row.required_qty || row.target_qty || 0),
                issued_qty: Number(row.issued_to_wo_qty ?? row.issued_qty ?? 0),
                remaining_qty: Number(row.wo_balance_qty || 0),
                available_qty: Number(row.available_qty || 0),
                shortage_qty: Number(row.shortage_qty || 0),
                status: String(row.status || ''),
                base_unit_id: row.base_unit_id ?? null,
                base_unit_name: row.base_unit_name ?? null,
                wo_item_id: row.wo_item_id ?? null,
                source_type: row.source_type,
                line_label: row.line_label ?? null,
                remarks: String(row.remarks || ''),
                uom_conversions: Array.isArray(row.uom_conversions) ? row.uom_conversions : [],
            }));
        return existingRows;
    }, [materialsPayload?.materials]);

    const issueOptionMap = useMemo(() => {
        const map = new Map<string, IssueMaterialOption>();
        issueMaterialOptions.forEach((option) => map.set(option.key, option));
        return map;
    }, [issueMaterialOptions]);
    const issuePickerOptions = useMemo<PickerOption<string>[]>(() => (
        issueMaterialOptions.map((option) => ({
            value: option.key,
            label: option.label,
            description: option.line_label || (option.row_type === 'UNPLANNED' ? 'Manual material issued to WO' : 'Issued to WO balance'),
            badges: [
                { label: option.source_type || (option.row_type === 'UNPLANNED' ? 'Manual' : 'Planned'), tone: option.row_type === 'UNPLANNED' ? 'blue' : 'slate' },
                { label: `WO balance ${formatQty(option.remaining_qty)}`, tone: Number(option.remaining_qty || 0) > 0 ? 'green' : 'amber' },
            ],
        }))
    ), [issueMaterialOptions]);

    const inventoryItemMap = useMemo(() => {
        const map = new Map<number, Item>();
        items.forEach((item) => map.set(Number(item.id), item));
        return map;
    }, [items]);

    const getOptionBaseUnitName = (option?: IssueMaterialOption) => {
        if (!option) return '';
        return option.base_unit_name || inventoryItemMap.get(Number(option.item_id))?.uom || '';
    };

    const getIssueUomOptions = (option?: IssueMaterialOption): IssueUomOption[] => {
        if (!option) return [];
        const baseUnitName = getOptionBaseUnitName(option) || 'Base UOM';
        const result: IssueUomOption[] = [];
        const seen = new Set<string>();
        const baseUnitId = option.base_unit_id ? String(option.base_unit_id) : '';
        if (baseUnitId) {
            result.push({ unitId: baseUnitId, label: baseUnitName, factorToBase: 1, isBase: true });
            seen.add(baseUnitId);
        }
        (option.uom_conversions || []).forEach((row) => {
            const unitId = String(row.from_unit_id || '');
            if (!unitId || seen.has(unitId)) return;
            result.push({
                unitId,
                label: row.from_unit_name || row.to_unit_name || baseUnitName,
                factorToBase: Number(row.factor_to_base || 1),
                isBase: Boolean(row.is_base) || (baseUnitId ? unitId === baseUnitId : Number(row.factor_to_base || 1) === 1),
            });
            seen.add(unitId);
        });
        if (result.length === 0) {
            result.push({ unitId: '', label: baseUnitName, factorToBase: 1, isBase: true });
        }
        return result;
    };

    const getIssueLinePreview = (line: typeof issueLines[number]) => {
        const selected = line.option_key ? issueOptionMap.get(line.option_key) : undefined;
        const qty = Number(line.qty || '0');
        const transactionUnitId = Number(line.transaction_unit_id || selected?.base_unit_id || 0);
        const conversion = (selected?.uom_conversions || []).find((row) => Number(row.from_unit_id) === transactionUnitId);
        const factor = Number(conversion?.factor_to_base || 1);
        return {
            factor,
            baseQty: qty * factor,
            baseUnitName: getOptionBaseUnitName(selected),
        };
    };

    const selectedUnplannedItem = useMemo(
        () => items.find((item) => String(item.id) === String(unplannedItemId)) || null,
        [items, unplannedItemId],
    );

    const flattenedPartRows = useMemo(
        () => flattenPartTree(materialsPayload?.parts),
        [materialsPayload?.parts],
    );
    const workOrderItemStateMap = useMemo(() => {
        const map = new Map<number, NonNullable<WorkOrder['items']>[number]>();
        (wo?.items || []).forEach((item) => {
            map.set(Number(item.wo_item_id), item);
        });
        return map;
    }, [wo?.items]);

    const selectedPartMeta = useMemo(() => {
        if (!selectedWoItemId) return null;
        const selectedId = Number(selectedWoItemId);
        return flattenedPartRows.find((part) => Number(part.wo_item_id) === selectedId) || null;
    }, [flattenedPartRows, selectedWoItemId]);
    const getItemName = (itemId?: number | null) => {
        const item = items.find((row) => Number(row.id) === Number(itemId || 0));
        return item ? `${item.code} - ${item.name}` : `Item ${itemId || '-'}`;
    };
    const recipeBoms = useMemo(() => boms.filter((bom) => (bom.components || []).length > 0), [boms]);
    const bomById = useMemo(() => new Map(recipeBoms.map((bom) => [Number(bom.id), bom])), [recipeBoms]);
    const getRecipeName = (bom?: BOM | null) => bom ? `${bom.bom_name || `Recipe ${bom.id}`} · Rev ${Number(bom.revision_no || 1)}` : '';
    const recipeOptions = useMemo<PickerOption<string>[]>(() => (
        recipeBoms.map((bom) => {
            const parentCount = (bom.components || []).filter((component) => !component.recipe_line_id).length;
            const childCount = (bom.structure_lines || []).length;
            return {
                value: String(bom.id),
                label: getRecipeName(bom),
                description: `${bom.finished_good_id ? `FG Ref: ${getItemName(bom.finished_good_id)}` : 'General recipe'} · ${parentCount} parent material(s), ${childCount} sub-part(s)`,
                badges: [
                    { label: `Recipe ${bom.id}`, tone: 'slate' },
                    { label: `${(bom.components || []).length} materials`, tone: 'green' },
                    ...(childCount ? [{ label: `${childCount} sub-parts`, tone: 'blue' as const }] : []),
                ],
            };
        })
    ), [recipeBoms, items]);
    const selectedLineRecipeBomId = Number(selectedPartMeta?.standard_recipe_bom_id || 0);
    const selectedItemRecipe = useMemo(
        () => recipeBoms.find((bom) => Number(bom.finished_good_id) === Number(selectedPartMeta?.item_id || 0)) || null,
        [recipeBoms, selectedPartMeta?.item_id],
    );
    const selectedEffectiveRecipe = selectedLineRecipeBomId > 0 ? bomById.get(selectedLineRecipeBomId) : selectedItemRecipe;
    const selectedLineHasRecipe = selectedLineRecipeBomId > 0 || !!selectedItemRecipe || Number(wo?.bom_id || 0) > 0;
    const selectedRecipeHasChildren = selectedEffectiveRecipe
        ? (selectedEffectiveRecipe.structure_lines || []).length > 0
        : false;

    const parentPartOptions = useMemo(
        () => (materialsPayload?.parts || []).filter((part) => String(part.line_role || 'LINE').toUpperCase() !== 'CHILD'),
        [materialsPayload?.parts],
    );

    const generatedSubPartPreview = useMemo(() => {
        if (editingSubPartId) {
            return String(workOrderItemStateMap.get(Number(editingSubPartId))?.sub_part_no || '').trim();
        }
        const parentId = Number(subPartParentId || '0');
        if (!parentId) return '';
        const parent = parentPartOptions.find((part) => Number(part.wo_item_id) === parentId);
        if (!parent) return '';
        const baseCode = String(parent.sub_part_no || '').trim()
            || String(parent.item_code || '').trim()
            || (Number(parent.estimate_part_id || 0) > 0 ? `PART-${Number(parent.estimate_part_id)}` : `LINE-${Number(parent.wo_item_id)}`);
        return `${baseCode}-SP${String((Number(parent.child_count || 0) + 1)).padStart(2, '0')}`;
    }, [editingSubPartId, parentPartOptions, subPartParentId, workOrderItemStateMap]);
    const selectedEntryDetail = useMemo(
        () => (dailyPayload?.entries || []).find((entry) => Number(entry.entry_id) === Number(selectedEntryId)) || null,
        [dailyPayload?.entries, selectedEntryId],
    );

    const computedLaborCost = useMemo(() => {
        const hours = Number(laborHours || '0');
        const rate = Number(hourlyRate || '0');
        const overrideRaw = laborOverride.trim();
        if (overrideRaw !== '') {
            const overrideAmount = Number(overrideRaw);
            return Number.isFinite(overrideAmount) && overrideAmount >= 0 ? overrideAmount : 0;
        }
        if (!Number.isFinite(hours) || hours <= 0) return 0;
        if (!Number.isFinite(rate) || rate <= 0) return 0;
        return hours * rate;
    }, [laborHours, hourlyRate, laborOverride]);

    const computedInstallationLaborCost = useMemo(() => {
        const hours = Number(installationLaborHours || '0');
        const rate = Number(installationHourlyRate || '0');
        const overrideRaw = installationLaborOverride.trim();
        if (overrideRaw !== '') {
            const overrideAmount = Number(overrideRaw);
            return Number.isFinite(overrideAmount) && overrideAmount >= 0 ? overrideAmount : 0;
        }
        if (!Number.isFinite(hours) || hours <= 0) return 0;
        if (!Number.isFinite(rate) || rate <= 0) return 0;
        return hours * rate;
    }, [installationLaborHours, installationHourlyRate, installationLaborOverride]);

    const computedDirectCostTotal = useMemo(
        () => directCostLines.reduce((sum, line) => sum + (Number(line.amount || '0') || 0), 0),
        [directCostLines],
    );

    const completionSummary = useMemo(() => {
        const entries = dailyPayload?.entries || [];
        const entriesCount = entries.length;
        const issueLinesCount = entries.reduce((sum, entry) => sum + (entry.issue_lines || []).length, 0);
        const issueCostTotal = entries.reduce(
            (sum, entry) => sum + (entry.issue_lines || []).reduce((lineSum, line) => lineSum + Number(line.line_cost || 0), 0),
            0,
        );
        const laborHoursTotal = Number(
            materialsPayload?.snapshot?.total_labor_hours ??
            entries.reduce((sum, entry) => sum + Number(entry.labor_hours || 0), 0)
        );
        const laborLogged = Number(
            materialsPayload?.snapshot?.total_labor_cost ??
            entries.reduce((sum, entry) => sum + Number(entry.labor_cost || 0), 0)
        );
        const overheadLogged = Number(
            materialsPayload?.snapshot?.total_overhead_cost ??
            entries.reduce((sum, entry) => sum + Number(entry.overhead_cost || 0), 0)
        );
        const installationLaborLogged = Number(
            materialsPayload?.snapshot?.total_installation_labor_cost ??
            entries.reduce((sum, entry) => sum + Number(entry.installation_labor_cost || 0), 0)
        );

        return {
            target_output_qty: Number(materialsPayload?.snapshot?.target_output_qty ?? materialsPayload?.planned_qty ?? wo?.quantity ?? 0),
            produced_output_qty: Number(materialsPayload?.snapshot?.produced_output_qty ?? 0),
            remaining_output_qty: Number(materialsPayload?.snapshot?.remaining_output_qty ?? 0),
            progress_percent: Number(wo?.progress_percent ?? 0),
            entries_count: entriesCount,
            issue_lines_count: issueLinesCount,
            issue_cost_total: issueCostTotal,
            labor_hours_total: laborHoursTotal,
            labor_cost_logged: laborLogged,
            installation_labor_cost_logged: installationLaborLogged,
            overhead_cost_logged: overheadLogged,
            extra_cost_logged: Number(materialsPayload?.snapshot?.total_extra_cost ?? 0),
            pending_material_lines: Number(materialsPayload?.snapshot?.pending_material_lines_count ?? materialsPayload?.pending_planned_count ?? 0),
            shortage_material_lines: Number(materialsPayload?.snapshot?.shortage_material_lines_count ?? 0),
            open_material_balance_count: Number(materialsPayload?.snapshot?.open_material_balance_count ?? 0),
            open_material_balance_qty: Number(materialsPayload?.snapshot?.open_material_balance_qty ?? 0),
            open_material_balances: materialsPayload?.snapshot?.open_material_balances || [],
            current_total_cost: Number(wo?.total_cost || 0),
        };
    }, [dailyPayload?.entries, materialsPayload?.planned_qty, materialsPayload?.pending_planned_count, materialsPayload?.snapshot, wo?.progress_percent, wo?.quantity, wo?.total_cost]);

    useEffect(() => {
        const defaultRate = Number(materialsPayload?.snapshot?.default_hourly_rate ?? 0);
        if (defaultRate > 0 && Number(hourlyRate || '0') <= 0) {
            setHourlyRate(defaultRate.toFixed(2));
        }
    }, [materialsPayload?.snapshot?.default_hourly_rate, hourlyRate]);

    useEffect(() => {
        if (!selectedPartMeta) return;
        const suggestedWeight = Number(selectedPartMeta.default_output_weight_kgs || 0);
        if (
            String(selectedPartMeta.production_mode || '').toUpperCase() === 'WEIGHT_BASED'
            && suggestedWeight > 0
            && Number(outputWeightKgs || '0') <= 0
        ) {
            setOutputWeightKgs(suggestedWeight.toFixed(3));
        }
        if (!subPartUom && selectedPartMeta.uom) {
            setSubPartUom(selectedPartMeta.uom);
        }
    }, [outputWeightKgs, selectedPartMeta, subPartUom]);

    useEffect(() => {
        if ((String(selectedPartMeta?.production_mode || '').toUpperCase() === 'WEIGHT_BASED' || !selectedLineHasRecipe) && autoBackflushBom) {
            setAutoBackflushBom(false);
        }
        if (!selectedRecipeHasChildren && includeChildRecipeMaterials) {
            setIncludeChildRecipeMaterials(false);
        }
    }, [autoBackflushBom, includeChildRecipeMaterials, selectedLineHasRecipe, selectedPartMeta, selectedRecipeHasChildren]);

    useEffect(() => {
        if (!woId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        let cancelled = false;

        api.manufacturing.getWorkOrder(woId)
            .then((woResult) => {
                if (cancelled) return;
                setWO(woResult);
                setLoading(false);

                void Promise.allSettled([
                    api.manufacturing.getWorkOrderMaterials(woId),
                    api.manufacturing.listWorkOrderDailyEntries(woId),
                    api.inventory.list(),
                    api.manufacturing.listBoms(),
                    loadWorkflowMeta(woId),
                ]).then((results) => {
                    if (cancelled) return;
                    const [materialsResult, entriesResult, itemsResult, bomsResult] = results;

                    if (materialsResult.status === 'fulfilled') {
                        setMaterialsPayload(materialsResult.value);
                    } else {
                        console.error(materialsResult.reason);
                        setMaterialsPayload(null);
                    }

                    if (entriesResult.status === 'fulfilled') {
                        setDailyPayload(entriesResult.value);
                    } else {
                        console.error(entriesResult.reason);
                        setDailyPayload(null);
                    }

                    if (itemsResult.status === 'fulfilled') {
                        setItems(itemsResult.value || []);
                    } else {
                        console.error(itemsResult.reason);
                        setItems([]);
                    }

                    if (bomsResult.status === 'fulfilled') {
                        setBoms(bomsResult.value || []);
                    } else {
                        console.error(bomsResult.reason);
                        setBoms([]);
                    }
                });
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(err);
                setWO(null);
                setMaterialsPayload(null);
                setDailyPayload(null);
                setItems([]);
                setBoms([]);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [woId]);

    const loadWorkflowMeta = async (woId: number) => {
        try {
            const [definition, actions, historyResult] = await Promise.all([
                legacyApi.workflow.getDefinition('WORK_ORDER'),
                legacyApi.workflow.getNextActions('WORK_ORDER', woId),
                legacyApi.workflow.getHistory('WORK_ORDER', woId),
            ]);
            setWorkflowDefinition(definition);
            setNextActions(actions.next_actions || []);
            setHistory(historyResult.history || []);
        } catch (error) {
            console.error('Failed to load workflow metadata', error);
        }
    };

    const reloadWorkOrderData = () => {
        if (!woId) return;
        Promise.all([
            api.manufacturing.getWorkOrder(woId),
            api.manufacturing.getWorkOrderMaterials(woId),
            api.manufacturing.listWorkOrderDailyEntries(woId),
            api.manufacturing.listBoms(),
            loadWorkflowMeta(woId),
        ])
            .then(([woResult, materialResult, entriesResult, bomsResult]) => {
                setWO(woResult);
                setMaterialsPayload(materialResult);
                setDailyPayload(entriesResult);
                setBoms(bomsResult);
            })
            .catch((err) => {
                console.error(err);
                toast.error('Failed to refresh work order');
            });
    };

    const showDailyEntryError = (err: any, fallback: string) => {
        const detail = err?.detail;
        if (detail && typeof detail === 'object' && Array.isArray(detail.line_errors)) {
            const first = detail.line_errors[0];
            const label = String(first?.line_label || `Line ${Number(first?.line_index || 0) + 1}`);
            const firstMessage = String(first?.errors?.[0] || '').trim();
            const requested = Number(first?.requested_qty || 0);
            const available = Number(first?.available_qty || 0);
            const hasStockContext = Number.isFinite(requested) && Number.isFinite(available) && (requested > 0 || available > 0);
            const stockContext = hasStockContext ? ` (requested ${formatQty(requested)}, available ${formatQty(available)})` : '';
            const message = firstMessage ? `${label}: ${firstMessage}${stockContext}` : (detail.message || fallback);
            toast.error(message);
            return;
        }
        toast.error(err?.message || fallback);
    };

    const handleQuickIssueRow = async (row: any) => {
        if (!wo) return;
        if (simpleModeEnabled) {
            toast.error('Production issue posting is disabled while this work order is on Simple flow.');
            return;
        }
        const rowKey = getMaterialRowKey(row);
        const qtyRaw = quickIssueQtyByRow[rowKey] ?? (Number(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0) > 0 ? String(Number(row.remaining_to_issue_qty ?? row.remaining_qty ?? 0)) : '0');
        const qty = Number(qtyRaw || '0');
        if (!Number.isFinite(qty) || qty <= 0) {
            toast.error('Enter a valid issue quantity');
            return;
        }

        const remarks = ((quickIssueRemarksByRow[rowKey] ?? String(row.remarks || '')) || '').trim();

        try {
            setEntryBusy(true);
            await api.manufacturing.postMaterialIssues({
                movement_date: entryDate,
                lines: [{
                    wo_id: wo.wo_id,
                    item_id: Number(row.item_id),
                    qty,
                    transaction_unit_id: row.base_unit_id || undefined,
                    row_type: row.row_type === 'UNPLANNED' ? 'UNPLANNED' : 'PLANNED',
                    source_type: row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE'),
                    source_ref: row.source_ref || undefined,
                    wo_item_id: row.wo_item_id || undefined,
                    remarks: remarks || undefined,
                    material_row_id: row.row_type === 'UNPLANNED' && typeof row.row_id === 'number' ? Number(row.row_id) : undefined,
                }],
            });
            toast.success('Material issued to WO balance');
            setQuickIssueQtyByRow((prev) => ({ ...prev, [rowKey]: '' }));
            if (row.row_type === 'UNPLANNED') {
                setQuickIssueRemarksByRow((prev) => {
                    const next = { ...prev };
                    delete next[rowKey];
                    return next;
                });
            }
            reloadWorkOrderData();
        } catch (err: any) {
            showDailyEntryError(err, 'Failed to issue material to WO');
        } finally {
            setEntryBusy(false);
        }
    };

    const handleReturnMaterialRow = async (row: any) => {
        if (!wo) return;
        if (simpleModeEnabled) {
            toast.error('Production issue posting is disabled while this work order is on Simple flow.');
            return;
        }
        const rowKey = getMaterialRowKey(row);
        const qtyRaw = quickIssueQtyByRow[rowKey] ?? String(row.wo_balance_qty || '0');
        const qty = Number(qtyRaw || '0');
        if (!Number.isFinite(qty) || qty <= 0) {
            toast.error('Enter a valid return quantity');
            return;
        }
        if (qty > Number(row.wo_balance_qty || 0) + 0.000001) {
            toast.error('Return quantity cannot exceed WO balance');
            return;
        }

        try {
            setEntryBusy(true);
            await api.manufacturing.postMaterialReturns({
                movement_date: entryDate,
                lines: [{
                    wo_id: wo.wo_id,
                    item_id: Number(row.item_id),
                    qty,
                    transaction_unit_id: row.base_unit_id || undefined,
                    row_type: row.row_type === 'UNPLANNED' ? 'UNPLANNED' : 'PLANNED',
                    source_type: row.source_type || (row.row_type === 'UNPLANNED' ? 'MANUAL' : 'ESTIMATE'),
                    source_ref: row.source_ref || undefined,
                    wo_item_id: row.wo_item_id || undefined,
                    material_row_id: row.row_type === 'UNPLANNED' && typeof row.row_id === 'number' ? Number(row.row_id) : undefined,
                }],
            });
            toast.success('Unused material returned to store');
            setQuickIssueQtyByRow((prev) => ({ ...prev, [rowKey]: '' }));
            reloadWorkOrderData();
        } catch (err: any) {
            showDailyEntryError(err, 'Failed to return material');
        } finally {
            setEntryBusy(false);
        }
    };

    const handleAddUnplannedMaterial = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!wo) return;
        if (simpleModeEnabled) {
            toast.error('Production issue posting is disabled while this work order is on Simple flow.');
            return;
        }
        const itemId = Number(unplannedItemId);
        const remarks = unplannedRemarks.trim();
        const defaultQty = Number(unplannedDefaultQty || '0');

        if (!itemId) {
            toast.error('Select a material');
            return;
        }
        try {
            setEntryBusy(true);
            await api.manufacturing.addUnplannedMaterial(wo.wo_id, {
                item_id: itemId,
                default_qty: Number.isFinite(defaultQty) ? defaultQty : 0,
                remarks,
            });
            toast.success('Material added');
            setUnplannedOpen(false);
            setUnplannedItemId('');
            setUnplannedDefaultQty('0');
            setUnplannedRemarks('');
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to add material');
        } finally {
            setEntryBusy(false);
        }
    };

    const handleAddIssueLine = () => {
        setIssueLines((prev) => [...prev, emptyIssueLine()]);
        dailyEntryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleAddDirectCostLine = () => {
        setDirectCostLines((prev) => [
            ...prev,
            { key: `${Date.now()}-${Math.random()}`, cost_type: 'TRANSPORT', amount: '0', remarks: '' },
        ]);
    };

    const resetSubPartForm = () => {
        setEditingSubPartId(null);
        setSubPartOpen(false);
        setSubPartParentId('');
        setSubPartDescription('');
        setSubPartQty('0');
        setSubPartUom('Nos');
        setSubPartDimensions('');
        setSubPartThickness('');
        setSubPartColor('');
        setSubPartFinish('');
        setSubPartLineRemarks('');
        setSubPartCompletionDate('');
    };

    const focusDailyEntryForLine = (woItemId: number) => {
        setSelectedWoItemId(String(woItemId));
        setSelectedEntryId(null);
        window.requestAnimationFrame(() => {
            dailyEntryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    };

    const hydrateEntryForm = (entry: WorkOrderDailyEntriesListResponse['entries'][number]) => {
        const targetLine = entry.wo_item_id ? flattenedPartRows.find((part) => Number(part.wo_item_id) === Number(entry.wo_item_id)) : null;
        const requestedOutput = Math.abs(Number(entry.output_qty || 0));
        const remainingOutput = Number(targetLine?.remaining_qty || 0);
        const allowOriginalOutput = requestedOutput > 0 && remainingOutput >= requestedOutput - 1e-6;
        setSelectedWoItemId(entry.wo_item_id ? String(entry.wo_item_id) : '');
        setEntryDate(entry.entry_date || new Date().toISOString().slice(0, 10));
        setOutputQty(allowOriginalOutput ? String(requestedOutput) : '0');
        setOutputWeightKgs(allowOriginalOutput ? String(Math.abs(Number(entry.output_weight_kgs || 0))) : '0');
        setAutoBackflushBom(false);
        setLaborHours(String(Math.abs(Number(entry.labor_hours || 0))));
        setHourlyRate(String(Number(entry.hourly_rate || 0)));
        setLaborOverride('');
        setInstallationLaborHours(String(Math.abs(Number(entry.installation_labor_hours || 0))));
        setInstallationHourlyRate(String(Number(entry.installation_hourly_rate || 0)));
        setInstallationLaborOverride('');
        setOverheadCost(String(Math.abs(Number(entry.overhead_cost || 0))));
        setEntryRemarks(entry.remarks || '');
        setIssueLines(
            (entry.issue_lines || []).length > 0
                ? (entry.issue_lines || []).map((line) => ({
                    key: `${Date.now()}-${Math.random()}`,
                    option_key: '',
                    item_id: String(line.item_id || ''),
                    qty: String(Math.abs(Number(line.qty || 0))),
                    transaction_unit_id: line.transaction_unit_id ? String(line.transaction_unit_id) : '',
                    row_type: (line.row_type || 'PLANNED') as 'PLANNED' | 'UNPLANNED',
                    remarks: line.remarks || '',
                    material_row_id: line.material_row_id || undefined,
                }))
                : [emptyIssueLine()],
        );
        const replaceableDirectCosts = (entry.cost_lines || [])
            .filter((line) => !['LABOR', 'INSTALLATION_LABOR', 'OVERHEAD'].includes(String(line.cost_type || '').toUpperCase()));
        setDirectCostLines(
            replaceableDirectCosts.length > 0
                ? replaceableDirectCosts.map((line) => ({
                    key: `${Date.now()}-${Math.random()}`,
                    cost_type: (DIRECT_COST_TYPES.has(String(line.cost_type || '').toUpperCase() as DirectCostType)
                        ? String(line.cost_type || '').toUpperCase()
                        : 'OTHER') as DirectCostDraft['cost_type'],
                    amount: String(Math.abs(Number(line.amount || 0))),
                    remarks: line.remarks || '',
                }))
                : [{ key: 'direct-1', cost_type: 'SUBCON', amount: '0', remarks: '' }],
        );
        if (requestedOutput > 0 && !allowOriginalOutput) {
            toast.warning('Output quantity was reset to 0 for replacement because this line has no remaining output. Adjust costs/materials and post without output.');
        }
        dailyEntryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleCreateSubPart = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!wo) return;
        const parentId = Number(subPartParentId || '0');
        const quantity = Number(subPartQty || '0');
        const description = subPartDescription.trim();
        if (!parentId) {
            toast.error('Select a parent part');
            return;
        }
        if (!description) {
            toast.error('Enter sub-part description');
            return;
        }
        if (!Number.isFinite(quantity) || quantity <= 0) {
            toast.error('Enter a valid sub-part quantity');
            return;
        }
        try {
            setEntryBusy(true);
            if (editingSubPartId) {
                await api.manufacturing.updateWorkOrderItem(wo.wo_id, editingSubPartId, {
                    description,
                    quantity,
                    uom: subPartUom.trim() || undefined,
                });
                await api.manufacturing.updateWorkOrderItemMetadata(wo.wo_id, editingSubPartId, {
                    dimensions: subPartDimensions.trim() || undefined,
                    thickness: subPartThickness.trim() || undefined,
                    color: subPartColor.trim() || undefined,
                    finish: subPartFinish.trim() || undefined,
                    line_remarks: subPartLineRemarks.trim() || undefined,
                    planned_completion_date: subPartCompletionDate || undefined,
                });
                toast.success('Sub-part updated');
            } else {
                await api.manufacturing.createWorkOrderItem(wo.wo_id, {
                    parent_wo_item_id: parentId,
                    description,
                    quantity,
                    uom: subPartUom.trim() || undefined,
                    dimensions: subPartDimensions.trim() || undefined,
                    thickness: subPartThickness.trim() || undefined,
                    color: subPartColor.trim() || undefined,
                    finish: subPartFinish.trim() || undefined,
                    line_remarks: subPartLineRemarks.trim() || undefined,
                    planned_completion_date: subPartCompletionDate || undefined,
                });
                toast.success('Sub-part created');
            }
            resetSubPartForm();
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || `Failed to ${editingSubPartId ? 'update' : 'create'} sub-part`);
        } finally {
            setEntryBusy(false);
        }
    };

    const handleEditSubPart = (woItemId: number) => {
        const item = workOrderItemStateMap.get(Number(woItemId));
        if (!item) return;
        setEditingSubPartId(Number(item.wo_item_id));
        setSubPartOpen(true);
        setSubPartParentId(String(item.parent_wo_item_id || ''));
        setSubPartDescription(item.description || '');
        setSubPartQty(String(Number(item.quantity || 0)));
        setSubPartUom(String(item.uom || 'Nos'));
        setSubPartDimensions(String(item.dimensions || ''));
        setSubPartThickness(String(item.thickness || ''));
        setSubPartColor(String(item.color || ''));
        setSubPartFinish(String(item.finish || ''));
        setSubPartLineRemarks(String(item.line_remarks || ''));
        setSubPartCompletionDate(String(item.planned_completion_date || '').slice(0, 10));
    };

    const handleApplyRecipe = async (woItemId: number) => {
        if (!wo) return;
        const bomId = Number(recipeSelectionByLine[woItemId] || flattenedPartRows.find((part) => Number(part.wo_item_id) === woItemId)?.standard_recipe_bom_id || 0);
        if (!bomId) {
            toast.error('Select a standard recipe');
            return;
        }
        try {
            setRecipeBusyLineId(woItemId);
            const result = await api.manufacturing.applyWorkOrderStandardRecipe(wo.wo_id, woItemId, {
                standard_recipe_bom_id: bomId,
                create_sub_parts: !!recipeCreateChildrenByLine[woItemId],
            });
            toast.success(result.created_sub_parts?.length ? `Recipe applied and ${result.created_sub_parts.length} sub-part(s) created` : 'Recipe applied');
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to apply standard recipe');
        } finally {
            setRecipeBusyLineId(null);
        }
    };

    const openLineDetailsEditor = (woItemId: number) => {
        const item = workOrderItemStateMap.get(Number(woItemId));
        if (!item) return;
        setEditingLineDetailsId(Number(item.wo_item_id));
        setLineDetailsDraft({
            dimensions: String(item.dimensions || ''),
            thickness: String(item.thickness || ''),
            color: String(item.color || ''),
            finish: String(item.finish || ''),
            line_remarks: String(item.line_remarks || ''),
            planned_completion_date: String(item.planned_completion_date || '').slice(0, 10),
        });
    };

    const handleSaveLineDetails = async () => {
        if (!wo || !editingLineDetailsId) return;
        try {
            setChildActionBusyId(editingLineDetailsId);
            await api.manufacturing.updateWorkOrderItemMetadata(wo.wo_id, editingLineDetailsId, {
                dimensions: String(lineDetailsDraft.dimensions || '').trim() || undefined,
                thickness: String(lineDetailsDraft.thickness || '').trim() || undefined,
                color: String(lineDetailsDraft.color || '').trim() || undefined,
                finish: String(lineDetailsDraft.finish || '').trim() || undefined,
                line_remarks: String(lineDetailsDraft.line_remarks || '').trim() || undefined,
                planned_completion_date: lineDetailsDraft.planned_completion_date || undefined,
            });
            toast.success('Line details updated');
            setEditingLineDetailsId(null);
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update line details');
        } finally {
            setChildActionBusyId(null);
        }
    };

    const handleDeleteSubPart = async (woItemId: number) => {
        if (!wo) return;
        const ok = await confirm('Delete this sub-part?', {
            description: 'Only child lines without postings can be deleted.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        try {
            setChildActionBusyId(Number(woItemId));
            await api.manufacturing.deleteWorkOrderItem(wo.wo_id, Number(woItemId));
            toast.success('Sub-part deleted');
            if (editingSubPartId === Number(woItemId)) {
                resetSubPartForm();
            }
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to delete sub-part');
        } finally {
            setChildActionBusyId(null);
        }
    };

    const handleReverseDailyEntry = async (entryId: number) => {
        if (!wo) return;
        const ok = await confirm(`Reverse daily entry #${entryId}?`, {
            description: 'This posts a reversal entry and keeps the original entry in the audit trail.',
            confirmText: 'Reverse Entry',
            cancelText: 'Cancel',
        });
        if (!ok) return;
        try {
            setEntryActionBusyId(Number(entryId));
            const result = await api.manufacturing.reverseWorkOrderDailyEntry(wo.wo_id, Number(entryId));
            toast.success(`Daily entry reversed as #${result.entry_id}`);
            setSelectedEntryId(Number(result.entry_id));
            reloadWorkOrderData();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to reverse daily entry');
        } finally {
            setEntryActionBusyId(null);
        }
    };

    const handlePostDailyEntry = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!wo) return;
        if (simpleModeEnabled) {
            toast.error('Production posting is disabled while this work order is on Simple flow.');
            return;
        }

        const parsedLines = issueLines
            .map((line) => ({
                item_id: Number(line.item_id),
                qty: Number(line.qty),
                transaction_unit_id: line.transaction_unit_id ? Number(line.transaction_unit_id) : undefined,
                row_type: line.row_type,
                remarks: line.remarks.trim() || undefined,
                material_row_id: line.material_row_id,
                wo_item_id: line.wo_item_id || undefined,
                source_type: line.source_type,
            }))
            .filter((line) => Number.isFinite(line.item_id) && line.item_id > 0 && Number.isFinite(line.qty) && line.qty > 0);

        const output = Number(outputQty || '0');
        const outputWeight = Number(outputWeightKgs || '0');
        const hours = Number(laborHours || '0');
        const rate = Number(hourlyRate || '0');
        const overrideRaw = laborOverride.trim();
        const laborOverrideValue = overrideRaw === '' ? undefined : Number(overrideRaw);
        const installationHours = Number(installationLaborHours || '0');
        const installationRate = Number(installationHourlyRate || '0');
        const installationOverrideRaw = installationLaborOverride.trim();
        const installationOverrideValue = installationOverrideRaw === '' ? undefined : Number(installationOverrideRaw);
        const overhead = Number(overheadCost || '0');
        const parsedDirectCosts = directCostLines
            .map((line) => ({
                cost_type: line.cost_type,
                amount: Number(line.amount || '0'),
                remarks: line.remarks.trim() || undefined,
            }))
            .filter((line) => Number.isFinite(line.amount) && line.amount > 0);
        if (
            output <= 0
            && parsedLines.length === 0
            && hours <= 0
            && (laborOverrideValue ?? 0) <= 0
            && installationHours <= 0
            && (installationOverrideValue ?? 0) <= 0
            && overhead <= 0
            && parsedDirectCosts.length === 0
        ) {
            toast.error('Add output, issue lines, labor, or direct job costs before posting');
            return;
        }
        if (output > 0 && !selectedWoItemId) {
            toast.error('Select part line for output posting');
            return;
        }
        if ((Number.isFinite(hours) && hours < 0) || (Number.isFinite(rate) && rate < 0)) {
            toast.error('Labor hours/rate cannot be negative');
            return;
        }
        if (laborOverrideValue !== undefined && (!Number.isFinite(laborOverrideValue) || laborOverrideValue < 0)) {
            toast.error('Labor override must be a non-negative number');
            return;
        }
        if (installationOverrideValue !== undefined && (!Number.isFinite(installationOverrideValue) || installationOverrideValue < 0)) {
            toast.error('Installation labor override must be a non-negative number');
            return;
        }
        if (hours > 0 && (!Number.isFinite(rate) || rate <= 0)) {
            toast.warning('Hourly rate is empty/zero; labor cost will remain 0 unless override is set.');
        }
        if (installationHours > 0 && (!Number.isFinite(installationRate) || installationRate <= 0)) {
            toast.warning('Installation hourly rate is empty/zero; installation labor cost will remain 0 unless override is set.');
        }
        if (
            output > 0
            && selectedPartMeta
            && String(selectedPartMeta.production_mode || '').toUpperCase() === 'WEIGHT_BASED'
            && (!Number.isFinite(outputWeight) || outputWeight <= 0)
        ) {
            toast.error('Output weight is required for weight-based production lines');
            return;
        }

        const payload: WorkOrderDailyEntryRequest = {
            entry_date: entryDate,
            wo_item_id: output > 0 ? Number(selectedWoItemId) : undefined,
            output_qty: Number.isFinite(output) ? output : 0,
            output_weight_kgs: Number.isFinite(outputWeight) ? outputWeight : 0,
            auto_backflush_bom: autoBackflushBom,
            include_child_recipe_materials: includeChildRecipeMaterials,
            labor_hours: Number.isFinite(hours) ? hours : 0,
            hourly_rate: Number.isFinite(rate) ? rate : 0,
            labor_cost_override: laborOverrideValue,
            labor_cost: 0,
            installation_labor_hours: Number.isFinite(installationHours) ? installationHours : 0,
            installation_hourly_rate: Number.isFinite(installationRate) ? installationRate : 0,
            installation_labor_cost_override: installationOverrideValue,
            installation_labor_cost: 0,
            overhead_cost: Number.isFinite(overhead) ? overhead : 0,
            remarks: entryRemarks.trim() || undefined,
            issue_lines: [],
            consume_lines: parsedLines,
            cost_lines: parsedDirectCosts,
        };

        try {
            setEntryBusy(true);
            const result = await api.manufacturing.postWorkOrderDailyEntry(wo.wo_id, payload);
            toast.success('Daily entry posted');
            (result.warnings || []).forEach((warning) => toast.warning(warning));
            setSelectedWoItemId('');
            setOutputQty('0');
            setOutputWeightKgs('0');
            setAutoBackflushBom(false);
            setIncludeChildRecipeMaterials(false);
            setLaborHours('0');
            setHourlyRate(materialsPayload?.snapshot?.default_hourly_rate ? Number(materialsPayload.snapshot.default_hourly_rate).toFixed(2) : '0');
            setLaborOverride('');
            setInstallationLaborHours('0');
            setInstallationHourlyRate('0');
            setInstallationLaborOverride('');
            setOverheadCost('0');
            setEntryRemarks('');
            setIssueLines([emptyIssueLine()]);
            setDirectCostLines([{ key: 'direct-1', cost_type: 'SUBCON', amount: '0', remarks: '' }]);
            reloadWorkOrderData();
        } catch (err: any) {
            showDailyEntryError(err, 'Failed to post daily entry');
        } finally {
            setEntryBusy(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading Work Order...</div>;
    if (!wo) return <div className="p-8 text-center text-red-500">Work Order not found</div>;
    const normalizedStatus = String(wo.status || '').replace(/\s+/g, '').toLowerCase();

    const handlePrint = () => {
        try {
            api.manufacturing.openWorkOrderPrint(wo.wo_id, true);
        } catch (e: any) {
            toast.error(e?.message || 'Failed to open work order print');
        }
    };

    const runWorkOrderPrecheck = async (action: 'start' | 'complete') => {
        try {
            const precheck = await legacyApi.workflow.precheck({
                entity: 'WORK_ORDER',
                entity_id: wo.wo_id,
                action,
            });
            const status = String(precheck.overall_status || 'pass').toLowerCase();
            if (status === 'fail') {
                toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Workflow precheck failed');
                return false;
            }
            if (status === 'warn') {
                const proceed = await confirm(`${action === 'start' ? 'Start' : 'Complete'} production with warnings?`, {
                    description: precheck.checks?.map((check) => `- ${check.message}`).join('\n') || 'Review precheck warnings.',
                    confirmText: action === 'start' ? 'Start Anyway' : 'Complete Anyway',
                    cancelText: 'Cancel',
                });
                if (!proceed) return false;
            }
            return true;
        } catch (err: any) {
            toast.error(err?.message || 'Failed to run workflow precheck');
            return false;
        }
    };

    const statusDefinition = workflowDefinition?.statuses?.find((status) => status.status_id === wo.status_id) ||
        (wo.status_id ? workflowDefinition?.statuses?.find(s => s.status_label.toLowerCase() === wo.status_id!.toString().toLowerCase()) : undefined); // fallback if ID mismatch

    return (
        <div className="space-y-6 w-full p-6 pb-12">
            <FlowEntryBanner
                title="Production Flow Guidance"
                pathLabel="WO Create -> Start -> Complete"
                nextHint="Track source lineage and complete WO to unlock finished-goods downstream flow."
                helpModule="production"
            />
            {simpleModeEnabled ? (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    {wo?.resolved_flow_mode || wo?.flow_mode
                        ? 'This linked sale order is using Simple flow. Sales can bypass production and auto-seed inventory, so this work-order page stays read-only.'
                        : 'Simplified flow mode is active globally. Sales can bypass production and auto-seed inventory, so this work-order page stays read-only.'}
                </div>
            ) : null}

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" onClick={() => navigate('/manufacturing')}>
                        <ArrowLeft className="h-4 w-4 mr-2" /> Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">{getOperatorWorkOrderLabel(wo)}</h1>
                        <p className="text-sm text-slate-500">Work Order #{wo.wo_id}</p>
                        <p className="text-slate-500">{getWorkOrderDescriptionSummary(wo)}</p>
                        <p className="text-xs text-slate-500">Flow Mode: {flowModeLabel}</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={handlePrint}>
                        <Printer className="w-4 h-4 mr-2" /> Print
                    </Button>
                    {(normalizedStatus === 'planned' || normalizedStatus === 'draft') ? (
                        <Button
                            variant="destructive"
                            disabled={actionBusy === 'delete'}
                            onClick={async () => {
                                if (actionBusy) return;
                                const ok = await confirm(`Delete ${wo.wo_number}?`, {
                                    description: 'Only Draft/Planned work orders can be deleted.',
                                    confirmText: 'Delete',
                                    cancelText: 'Cancel',
                                });
                                if (!ok) return;
                                setActionBusy('delete');
                                try {
                                    await api.manufacturing.deleteWorkOrder(wo.wo_id);
                                    toast.success('Work order deleted');
                                    navigate('/manufacturing');
                                } catch (e: any) {
                                    toast.error(e?.message || 'Failed to delete work order');
                                } finally {
                                    setActionBusy(null);
                                }
                            }}
                        >
                            <Trash2 className="w-4 h-4 mr-2" /> {actionBusy === 'delete' ? 'Deleting...' : 'Delete'}
                        </Button>
                    ) : null}
                    {!simpleModeEnabled && (normalizedStatus === 'planned' || normalizedStatus === 'draft') ? (
                        <Button
                            className="bg-blue-600"
                            disabled={actionBusy === 'start'}
                            onClick={async () => {
                                if (actionBusy) return;
                                setActionBusy('start');
                                try {
                                    const ok = await confirm('Start Production?', { confirmText: 'Start', cancelText: 'Cancel' });
                                    if (!ok) return;
                                    const result = await api.manufacturing.startWorkOrder(wo.wo_id);
                                    toast.success('Production started');
                                    (result?.warnings || []).forEach((warning: string) => toast.warning(warning));
                                    reloadWorkOrderData();
                                } catch (err: any) {
                                    toast.error(err?.message || 'Failed to start production');
                                } finally {
                                    setActionBusy(null);
                                }
                            }}
                        >
                            <Play className="w-4 h-4 mr-2" /> {actionBusy === 'start' ? 'Starting...' : 'Start Production'}
                        </Button>
                    ) : null}

                    {!simpleModeEnabled && (normalizedStatus === 'in-progress' || normalizedStatus === 'inprogress') ? (
                        <>
                            <Button
                                className="bg-green-600"
                                disabled={actionBusy === 'complete'}
                                onClick={() => setCompletionOpen(true)}
                            >
                                <CheckCircle className="w-4 h-4 mr-2" /> Complete
                            </Button>
                            <CompletionModal isOpen={completionOpen} onClose={() => setCompletionOpen(false)} summary={completionSummary} onConfirm={async (costs) => {
                                if (actionBusy) return;
                                setActionBusy('complete');
                                const precheckOk = await runWorkOrderPrecheck('complete');
                                if (!precheckOk) {
                                    setActionBusy(null);
                                    return;
                                }
                                try {
                                    const result = await api.manufacturing.completeWorkOrder(wo.wo_id, costs);
                                    toast.success('Work order completed');
                                    (result?.warnings || []).forEach((warning: string) => toast.warning(warning));
                                    setCompletionOpen(false);
                                    reloadWorkOrderData();
                                } catch (err: any) {
                                    toast.error(err?.message || 'Failed to complete work order');
                                } finally {
                                    setActionBusy(null);
                                }
                            }} />
                        </>
                    ) : null}
                </div>
            </div>

            <div className="space-y-6">
                <div className="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setWorkflowPanelOpen((prev) => !prev)}
                    >
                        {workflowPanelOpen ? (
                            <>
                                <PanelRightClose className="w-4 h-4 mr-2" /> Hide Workflow Panel
                            </>
                        ) : (
                            <>
                                <PanelRightOpen className="w-4 h-4 mr-2" /> Show Workflow Panel
                            </>
                        )}
                    </Button>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between gap-2">
                                <CardTitle>Production Control</CardTitle>
	                                {!simpleModeEnabled ? (
	                                    <Button size="sm" variant="outline" onClick={() => setSubPartOpen(true)}>
	                                        <Plus className="w-4 h-4 mr-1" /> Add Sub-part
	                                    </Button>
	                                ) : null}
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-8 gap-3 text-sm">
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Status</div>
                                    <div className="font-semibold">{wo.status}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Operational Target</div>
                                    <div className="font-semibold">{formatQty(materialsPayload?.snapshot?.target_output_qty ?? materialsPayload?.planned_qty ?? wo.quantity ?? 0)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Operational Produced</div>
                                    <div className="font-semibold">{formatQty(materialsPayload?.snapshot?.produced_output_qty ?? 0)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Operational Remaining</div>
                                    <div className="font-semibold">{formatQty(materialsPayload?.snapshot?.remaining_output_qty ?? 0)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Pending Material Lines</div>
                                    <div className="font-semibold">{materialsPayload?.snapshot?.pending_material_lines_count ?? materialsPayload?.pending_planned_count ?? 0}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Prod Labor</div>
                                    <div className="font-semibold">{formatMoney(materialsPayload?.snapshot?.total_labor_cost ?? 0)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Install Labor</div>
                                    <div className="font-semibold">{formatMoney(materialsPayload?.snapshot?.total_installation_labor_cost ?? 0)}</div>
                                </div>
                                <div className="rounded border p-3">
                                    <div className="text-slate-500 text-xs uppercase">Direct Costs</div>
                                    <div className="font-semibold">{formatMoney(materialsPayload?.snapshot?.total_extra_cost ?? 0)}</div>
                                </div>
                            </div>
                            <div className="rounded-md border p-2">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                    <div className="text-xs font-medium uppercase text-slate-500">Manufacturing Lines</div>
                                    <div className="text-[11px] text-slate-500">Use a line for daily output, or open Details to update production specs.</div>
                                </div>
                                {(materialsPayload?.parts || []).length > 0 ? (
                                    <div className="space-y-2">
                                        {flattenedPartRows.map((part) => (
                                            <div
                                                key={part.wo_item_id}
                                                className="grid grid-cols-1 gap-2 rounded border px-2 py-2 text-sm lg:grid-cols-[minmax(280px,2fr)_repeat(4,minmax(110px,0.75fr))] lg:items-start"
                                                style={{ marginLeft: `${part.depth * 14}px` }}
                                            >
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">{part.description}</span>
                                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{part.line_role || 'LINE'}</span>
                                                        {part.sub_part_no ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{part.sub_part_no}</span> : null}
                                                        {String(part.production_mode || '').toUpperCase() === 'WEIGHT_BASED' ? (
                                                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Weight Based</span>
                                                        ) : null}
                                                    </div>
                                                    <div className="text-[11px] text-slate-500">
                                                        Actual cost {formatMoney(part.total_actual_cost || 0)}
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-slate-600">
                                                        {part.dimensions ? <span className="rounded bg-slate-100 px-2 py-0.5">Dim: {part.dimensions}</span> : null}
                                                        {part.thickness ? <span className="rounded bg-slate-100 px-2 py-0.5">Thk: {part.thickness}</span> : null}
                                                        {part.color ? <span className="rounded bg-slate-100 px-2 py-0.5">Color: {part.color}</span> : null}
                                                        {part.finish ? <span className="rounded bg-slate-100 px-2 py-0.5">Finish: {part.finish}</span> : null}
                                                        {part.planned_completion_date ? <span className="rounded bg-slate-100 px-2 py-0.5">Due: {String(part.planned_completion_date).slice(0, 10)}</span> : null}
                                                    </div>
                                                    {part.line_remarks ? (
                                                        <div className="mt-1 text-xs text-slate-500">{part.line_remarks}</div>
                                                    ) : null}
                                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8"
                                                            onClick={() => openLineDetailsEditor(Number(part.wo_item_id))}
                                                            disabled={childActionBusyId === Number(part.wo_item_id)}
                                                        >
                                                            <Pencil className="w-4 h-4 mr-1" /> Details
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8"
                                                            onClick={() => focusDailyEntryForLine(Number(part.wo_item_id))}
                                                        >
                                                            Use in Entry
                                                        </Button>
                                                    </div>
                                                    {String(part.line_role || '').toUpperCase() !== 'CHILD' ? (
                                                        <div className="mt-2 grid grid-cols-1 gap-2 rounded border border-emerald-100 bg-emerald-50/60 p-2 md:grid-cols-[minmax(0,1fr)_auto]">
                                                            <div className="space-y-1">
                                                                <label className="text-[11px] font-medium text-emerald-900">Recipe</label>
                                                                <EntityTypeaheadPicker
                                                                    value={recipeSelectionByLine[Number(part.wo_item_id)] ?? String(part.standard_recipe_bom_id || '')}
                                                                    options={recipeOptions}
                                                                    placeholder="Search recipe name..."
                                                                    emptyText="No matching standard recipes"
                                                                    onChange={(value) => setRecipeSelectionByLine((prev) => ({ ...prev, [Number(part.wo_item_id)]: value }))}
                                                                />
                                                                {part.standard_recipe_bom_id ? (
                                                                    <div className="text-[11px] text-emerald-700">
                                                                        Applied: {getRecipeName(bomById.get(Number(part.standard_recipe_bom_id))) || `Recipe ${part.standard_recipe_bom_id}`}
                                                                    </div>
                                                                ) : null}
                                                                <label className="inline-flex items-center gap-2 text-[11px] text-emerald-800">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!recipeCreateChildrenByLine[Number(part.wo_item_id)]}
                                                                        onChange={(event) => setRecipeCreateChildrenByLine((prev) => ({ ...prev, [Number(part.wo_item_id)]: event.target.checked }))}
                                                                    />
                                                                    Create recipe sub-parts
                                                                </label>
                                                            </div>
                                                            <div className="flex items-end">
                                                                <Button
                                                                    type="button"
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="h-8"
                                                                    onClick={() => handleApplyRecipe(Number(part.wo_item_id))}
                                                                    disabled={recipeBusyLineId === Number(part.wo_item_id)}
                                                                >
                                                                    Apply
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    ) : part.standard_recipe_bom_id ? (
                                                        <div className="mt-2 text-[11px] text-emerald-700">
                                                            Recipe material group: {getRecipeName(bomById.get(Number(part.standard_recipe_bom_id))) || `Recipe ${part.standard_recipe_bom_id}`}
                                                        </div>
                                                    ) : null}
                                                    {String(part.line_role || '').toUpperCase() === 'CHILD' ? (
                                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                                            {(() => {
                                                                const childUiState = resolveChildUiState(part, workOrderItemStateMap);
                                                                return (
                                                                    <>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8"
                                                                onClick={() => handleEditSubPart(Number(part.wo_item_id))}
                                                                disabled={!childUiState.editable || childActionBusyId === Number(part.wo_item_id)}
                                                            >
                                                                <Pencil className="w-4 h-4 mr-1" /> Edit
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                className="h-8"
                                                                onClick={() => handleDeleteSubPart(Number(part.wo_item_id))}
                                                                disabled={!childUiState.deletable || childActionBusyId === Number(part.wo_item_id)}
                                                                title={childUiState.deletable ? 'Delete unused child item' : childUiState.deleteLockReason}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                                                            </Button>
                                                            {!childUiState.deletable ? (
                                                                <div className="w-full text-[11px] text-slate-500">
                                                                    Delete locked: {childUiState.deleteLockReason}
                                                                </div>
                                                            ) : null}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="rounded bg-slate-50 px-2 py-1">
                                                    <div className="text-[10px] uppercase text-slate-500">Target</div>
                                                    <div className="font-medium">{formatQty(part.target_qty || 0)} {part.uom || ''}</div>
                                                </div>
                                                <div className="rounded bg-slate-50 px-2 py-1">
                                                    <div className="text-[10px] uppercase text-slate-500">Produced</div>
                                                    <div className="font-medium">{formatQty(part.produced_qty || 0)}</div>
                                                </div>
                                                <div className="rounded bg-slate-50 px-2 py-1">
                                                    <div className="text-[10px] uppercase text-slate-500">Remaining</div>
                                                    <div className="font-medium">{formatQty(part.remaining_qty || 0)}</div>
                                                </div>
                                                <div className="rounded bg-slate-50 px-2 py-1">
                                                    <div className="text-[10px] uppercase text-slate-500">Weight</div>
                                                    <div className="font-medium">{formatWeight(part.output_weight_kgs || 0)} kg</div>
                                                    <div className="text-xs text-slate-500">
                                                        Prod {formatMoney(part.production_labor_cost || 0)} | Inst {formatMoney(part.installation_labor_cost || 0)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-slate-500">No part lines linked yet.</div>
                                )}
                            </div>
                            <div className="text-xs text-slate-500">
                                Pending Material Lines = material rows with remaining quantity.
                            </div>
                            {materialsPayload?.has_shortage ? (
                                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                    Planned shortages detected. Production can continue; issue materials daywise.
                                    {(materialsPayload?.snapshot?.shortage_material_lines_count ?? 0) > 0 ? ` Shortage lines: ${materialsPayload?.snapshot?.shortage_material_lines_count}.` : ''}
                                </div>
                            ) : (
                                <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                                    No current shortages in mapped materials.
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <div className="flex justify-between items-center gap-2">
                                <CardTitle>Materials</CardTitle>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={() => setUnplannedOpen(true)} disabled={simpleModeEnabled}>
                                        <Plus className="w-4 h-4 mr-1" /> Add Material
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="text-xs text-slate-500">
                                {simpleModeEnabled ? 'Production issue posting is disabled because the linked sale order is using Simple flow.' : 'Materials can come from estimate/recipe planning or be added manually during production.'}
                            </div>
                            <TableFilterBar
                                searchValue={state.q_materials}
                                onSearchChange={(value) => setFilter('q_materials', value)}
                                searchPlaceholder="Search materials..."
                                isDirty={isDirty && !!state.q_materials}
                                onClear={() => setFilter('q_materials', '')}
                                rightSlot={`${filteredMaterials.length} rows`}
                            />
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                {(['ALL', 'ESTIMATE', 'RECIPE', 'MANUAL'] as const).map((source) => (
                                    <button
                                        key={source}
                                        type="button"
                                        onClick={() => setMaterialSourceFilter(source)}
                                        className={`rounded-full border px-3 py-1 font-medium ${materialSourceFilter === source ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                                    >
                                        {source === 'ALL' ? 'All Sources' : source === 'ESTIMATE' ? 'Estimate' : source === 'RECIPE' ? 'Recipe' : 'Manual'}
                                    </button>
                                ))}
                                <select
                                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700"
                                    value={materialLineFilter}
                                    onChange={(event) => setMaterialLineFilter(event.target.value)}
                                >
                                    <option value="ALL">All Parts / Sub-parts</option>
                                    {flattenedPartRows.map((part) => (
                                        <option key={part.wo_item_id} value={String(part.wo_item_id)}>
                                            {part.depth > 0 ? `${'-- '.repeat(part.depth)}` : ''}{part.description}{part.sub_part_no ? ` [${part.sub_part_no}]` : ''}
                                        </option>
                                    ))}
                                </select>
                                {(materialSourceFilter !== 'ALL' || materialLineFilter !== 'ALL') ? (
                                    <button
                                        type="button"
                                        className="text-blue-600 hover:underline"
                                        onClick={() => {
                                            setMaterialSourceFilter('ALL');
                                            setMaterialLineFilter('ALL');
                                        }}
                                    >
                                        Clear material filters
                                    </button>
                                ) : null}
                            </div>
                            <CompactTable data={filteredMaterials} columns={requirementsColumns} keyField="row_id" />
                        </CardContent>
                    </Card>

                    <Card ref={dailyEntryRef}>
                        <CardHeader>
                            <CardTitle>Daily Entry</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {simpleModeEnabled ? (
                                <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                    This work order is on Simple flow. Production posting stays disabled so the bypassed sales path and provisional inventory handling remain consistent.
                                </div>
                            ) : (
	                            <form onSubmit={handlePostDailyEntry} className="space-y-4">
	                                <div className="rounded-md border p-3 space-y-3">
	                                    {renderSectionTitle('Work Done', 'Select the production line and enter the quantity or weight completed for this entry.')}
	                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div>
                                        <label className="text-sm font-medium">Date</label>
                                        <input type="date" className="w-full border rounded p-2 mt-1" value={entryDate} onChange={(event) => setEntryDate(event.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Part Line {Number(outputQty || '0') > 0 ? '*' : '(optional)'}</label>
                                        <select
                                            className="w-full border rounded p-2 mt-1"
                                            value={selectedWoItemId}
                                            onChange={(event) => setSelectedWoItemId(event.target.value)}
                                        >
                                            <option value="">Select part line...</option>
                                            {flattenedPartRows.map((part) => (
                                                <option key={part.wo_item_id} value={part.wo_item_id}>
                                                    {part.depth > 0 ? `${'— '.repeat(part.depth)}` : ''}{part.description}{part.sub_part_no ? ` [${part.sub_part_no}]` : ''} (Rem: {formatQty(part.remaining_qty || 0)})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Output Qty</label>
                                        <input type="number" min="0" step="0.001" className="w-full border rounded p-2 mt-1" value={outputQty} onChange={(event) => setOutputQty(event.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Output Weight (kg)</label>
                                        <input type="number" min="0" step="0.001" className="w-full border rounded p-2 mt-1" value={outputWeightKgs} onChange={(event) => setOutputWeightKgs(event.target.value)} />
                                    </div>
	                                </div>
	                                {selectedPartMeta ? (
	                                    <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                        <span className="font-medium mr-1">Selected Part:</span>
                                        {selectedPartMeta.description} | Target {formatQty(selectedPartMeta.target_qty || 0)} {selectedPartMeta.uom || ''} | Produced {formatQty(selectedPartMeta.produced_qty || 0)} | Remaining {formatQty(selectedPartMeta.remaining_qty || 0)}
	                                        {String(selectedPartMeta.production_mode || '').toUpperCase() === 'WEIGHT_BASED' ? ` | Weight-based line${Number(selectedPartMeta.default_output_weight_kgs || 0) > 0 ? ` | Default ${formatWeight(selectedPartMeta.default_output_weight_kgs || 0)} kg` : ''}` : ''}
	                                    </div>
	                                ) : null}
	                                {selectedLineHasRecipe && String(selectedPartMeta?.production_mode || '').toUpperCase() !== 'WEIGHT_BASED' ? (
	                                <label className="inline-flex items-start gap-2 rounded border bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
	                                    <input
	                                        type="checkbox"
	                                        className="mt-1"
	                                        checked={autoBackflushBom}
	                                        onChange={(event) => setAutoBackflushBom(event.target.checked)}
	                                        disabled={!selectedWoItemId}
	                                    />
	                                    <span>
	                                        <span className="font-medium">Use recipe materials automatically</span>
	                                        <span className="block text-xs text-emerald-700">
	                                            Recipe materials will be consumed with this output. Manual materials below are optional.
	                                        </span>
	                                    </span>
	                                </label>
	                                ) : null}
	                                {selectedRecipeHasChildren && String(selectedPartMeta?.line_role || '').toUpperCase() !== 'CHILD' ? (
	                                    <label className="inline-flex items-center gap-2 rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                        <input
                                            type="checkbox"
                                            checked={includeChildRecipeMaterials}
                                            onChange={(event) => setIncludeChildRecipeMaterials(event.target.checked)}
	                                            disabled={!autoBackflushBom}
	                                        />
	                                        Include sub-part materials
	                                    </label>
	                                ) : null}
	                                </div>
	                                <div className="rounded-md border p-3 space-y-3">
	                                    {renderSectionTitle('Time', 'Record production and installation hours separately for actual-cost control.')}
	                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
	                                    <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
	                                        Production Labor Cost: <span className="font-semibold">{formatMoney(computedLaborCost)}</span>
                                        {laborOverride.trim() ? ' (override applied)' : ''}
                                    </div>
                                    <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-700">
                                        Installation Labor Cost: <span className="font-semibold">{formatMoney(computedInstallationLaborCost)}</span>
	                                        {installationLaborOverride.trim() ? ' (override applied)' : ''}
	                                    </div>
	                                </div>
	                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
	                                    <div>
	                                        <label className="text-sm font-medium">Prod Labor Hrs</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={laborHours} onChange={(event) => setLaborHours(event.target.value)} />
	                                    </div>
	                                    <div>
	                                        <label className="text-sm font-medium">Prod Rate</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={hourlyRate} onChange={(event) => setHourlyRate(event.target.value)} />
	                                    </div>
	                                    <div>
	                                        <label className="text-sm font-medium">Prod Override</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={laborOverride} onChange={(event) => setLaborOverride(event.target.value)} placeholder="Use only for exceptions" />
	                                    </div>
	                                    <div>
	                                        <label className="text-sm font-medium">Install Labor Hrs</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={installationLaborHours} onChange={(event) => setInstallationLaborHours(event.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Install Rate</label>
                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={installationHourlyRate} onChange={(event) => setInstallationHourlyRate(event.target.value)} />
                                    </div>
                                    <div>
	                                        <label className="text-sm font-medium">Install Override</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={installationLaborOverride} onChange={(event) => setInstallationLaborOverride(event.target.value)} placeholder="Use only for exceptions" />
	                                    </div>
	                                    <div>
	                                        <label className="text-sm font-medium">Overhead Cost</label>
	                                        <input type="number" min="0" step="0.01" className="w-full border rounded p-2 mt-1" value={overheadCost} onChange={(event) => setOverheadCost(event.target.value)} />
	                                    </div>
	                                </div>
	                                <div className="text-xs text-slate-500">
	                                    Production and installation stay separate for actual-cost control. Weight is mandatory only on weight-based GRC lines.
	                                </div>
	                                </div>
	                                <div className="space-y-2 rounded-md border p-3">
	                                    {renderSectionTitle('Direct Costs', 'Capture subcontracting, transport, service, direct labour hire, or other direct job costs.')}
	                                    <div className="flex items-center justify-between">
	                                        <div className="text-sm font-semibold">Cost Lines</div>
                                        <Button type="button" size="sm" variant="outline" onClick={handleAddDirectCostLine}>
                                            <Plus className="w-4 h-4 mr-1" /> Add Cost
                                        </Button>
                                    </div>
                                    {directCostLines.map((line) => (
                                        <div key={line.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                                            <div className="md:col-span-3">
                                                <label className="text-xs text-slate-500">Cost Type</label>
                                                <select
                                                    className="w-full border rounded p-2"
                                                    value={line.cost_type}
                                                    onChange={(event) => setDirectCostLines((prev) => prev.map((row) => row.key === line.key ? {
                                                        ...row,
                                                        cost_type: event.target.value as DirectCostDraft['cost_type'],
                                                    } : row))}
                                                >
                                                    {DIRECT_COST_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className="text-xs text-slate-500">Amount</label>
                                                <input type="number" min="0" step="0.01" className="w-full border rounded p-2" value={line.amount} onChange={(event) => setDirectCostLines((prev) => prev.map((row) => row.key === line.key ? { ...row, amount: event.target.value } : row))} />
                                            </div>
                                            <div className="md:col-span-5">
                                                <label className="text-xs text-slate-500">Remarks</label>
                                                <input className="w-full border rounded p-2" value={line.remarks} onChange={(event) => setDirectCostLines((prev) => prev.map((row) => row.key === line.key ? { ...row, remarks: event.target.value } : row))} />
                                            </div>
                                            <div className="md:col-span-1">
                                                <Button type="button" variant="ghost" className="w-full" onClick={() => setDirectCostLines((prev) => prev.length <= 1 ? prev : prev.filter((row) => row.key !== line.key))}>
                                                    <Trash2 className="w-4 h-4 text-red-600" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="text-xs text-slate-500">
                                        Direct-cost total for this entry: <span className="font-semibold">{formatMoney(computedDirectCostTotal)}</span>
                                        <span className="block">Direct job costs affect production costing only. Supplier bills/payables must still be entered through finance.</span>
                                    </div>
                                </div>
		                                <div className="space-y-2 rounded-md border p-3">
                                            <div className="flex flex-wrap items-start justify-between gap-2 border-b pb-2">
                                                <div>
                                                    <div className="text-sm font-semibold text-slate-900">Materials Used</div>
                                                    <div className="text-xs text-slate-500">Add only the materials consumed in this entry. Notes are optional.</div>
                                                </div>
                                                <Button type="button" size="sm" variant="outline" onClick={handleAddIssueLine} disabled={simpleModeEnabled}>
                                                    <Plus className="w-4 h-4 mr-1" /> Add Material
                                                </Button>
                                            </div>
		                                    {issueLines.map((line, index) => (
	                                        <div key={line.key} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end border rounded p-2">
	                                            <div className="md:col-span-5">
	                                                <label className="text-xs text-slate-500">Material</label>
	                                                <EntityTypeaheadPicker
	                                                    value={line.option_key || ''}
	                                                    onChange={(event) => {
	                                                        const selected = issueOptionMap.get(event);
	                                                        const suggestedQty = selected && selected.remaining_qty > 0 ? String(selected.remaining_qty) : '0';
	                                                        const issueUomOptions = getIssueUomOptions(selected);
	                                                        setIssueLines((prev) => prev.map((row) => row.key === line.key ? {
	                                                            ...row,
	                                                            option_key: event,
	                                                            item_id: selected ? String(selected.item_id) : '',
	                                                            transaction_unit_id: issueUomOptions[0]?.unitId || '',
	                                                            row_type: selected?.row_type || row.row_type,
	                                                            material_row_id: selected?.material_row_id,
	                                                            wo_item_id: selected?.wo_item_id,
	                                                            source_type: selected?.source_type,
	                                                            qty: selected ? suggestedQty : row.qty,
	                                                            remarks: '',
	                                                        } : row));
	                                                    }}
	                                                    options={issuePickerOptions}
	                                                    placeholder="Search issued WO material to consume..."
	                                                    emptyText="No issued WO material balance found"
	                                                />
	                                            </div>
	                                            <div className="md:col-span-2">
	                                                <label className="text-xs text-slate-500">Source</label>
	                                                <div className="w-full border rounded p-2 bg-slate-50 text-sm text-slate-700">
	                                                    {line.row_type === 'UNPLANNED' ? 'Added Manually' : 'From Estimate / Recipe'}
	                                                </div>
	                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs text-slate-500">Qty</label>
                                                <input type="number" min="0" step="0.001" className="w-full border rounded p-2" value={line.qty} onChange={(event) => setIssueLines((prev) => prev.map((row) => row.key === line.key ? { ...row, qty: event.target.value } : row))} />
                                            </div>
                                            <div className="md:col-span-2">
                                                <label className="text-xs text-slate-500">Issue UOM</label>
                                                <select
                                                    value={line.transaction_unit_id || ''}
                                                    onChange={(event) => setIssueLines((prev) => prev.map((row) => row.key === line.key ? { ...row, transaction_unit_id: event.target.value } : row))}
                                                    className="w-full border rounded p-2"
                                                    disabled={!line.option_key}
                                                >
                                                    {getIssueUomOptions(line.option_key ? issueOptionMap.get(line.option_key) : undefined).map((option) => (
                                                        <option key={`${line.key}-${option.unitId || 'base'}`} value={option.unitId}>
                                                            {option.label}{option.isBase ? ' (Base)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
	                                            <div className="md:col-span-2">
	                                                <label className="text-xs text-slate-500">Note (optional)</label>
	                                                <input type="text" className="w-full border rounded p-2" value={line.remarks} onChange={(event) => setIssueLines((prev) => prev.map((row) => row.key === line.key ? { ...row, remarks: event.target.value } : row))} />
	                                            </div>
                                            <div className="md:col-span-1">
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    className="w-full"
                                                    onClick={() => setIssueLines((prev) => prev.length <= 1 ? prev : prev.filter((row) => row.key !== line.key))}
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-600" />
                                                </Button>
                                            </div>
                                            {line.option_key && issueOptionMap.get(line.option_key) ? (
                                                <div className="md:col-span-12 rounded border bg-slate-50 px-2 py-1 text-xs text-slate-600">
                                                    {(() => {
                                                        const selected = issueOptionMap.get(line.option_key)!;
	                                                        const targetLabel = selected.row_type === 'UNPLANNED' ? 'Manual target' : 'Required';
                                                        const preview = getIssueLinePreview(line);
                                                        const baseUnitName = getOptionBaseUnitName(selected);
                                                        return (
                                                            <>
                                                                In hand: <span className="font-medium">{formatQty(selected.available_qty)} {baseUnitName}</span>
                                                                {' | '}
                                                                {targetLabel}: <span className="font-medium">{formatQty(selected.required_qty)} {baseUnitName}</span>
                                                                {' | '}
                                                                Already issued: <span className="font-medium">{formatQty(selected.issued_qty)} {baseUnitName}</span>
                                                                {' | '}
                                                                Remaining: <span className="font-medium">{formatQty(selected.remaining_qty)} {baseUnitName}</span>
                                                                {' | '}
                                                                Base qty: <span className="font-medium">{formatQty(preview.baseQty)} {preview.baseUnitName || 'base'}</span>
                                                                {' | '}
                                                                Status: <span className="font-medium">{selected.status === 'SHORT' ? 'Insufficient' : selected.status === 'ISSUED' ? 'Issued' : 'Ready'}</span>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            ) : null}
                                        </div>
	                                    ))}
	                                </div>
	                                <div className="rounded-md border p-3 space-y-3">
	                                    {renderSectionTitle('Notes & Post')}
	                                    <div>
	                                        <label className="text-sm font-medium">Entry Notes (optional)</label>
	                                        <textarea className="w-full border rounded p-2 mt-1" rows={2} value={entryRemarks} onChange={(event) => setEntryRemarks(event.target.value)} />
	                                    </div>
	                                <div className="flex justify-end">
	                                    <Button type="submit" disabled={entryBusy}>{entryBusy ? 'Posting...' : 'Post Daily Entry'}</Button>
	                                </div>
	                                </div>
                            </form>
                            )}
                            <div className="border-t pt-4 space-y-3">
                                <TableFilterBar
                                    searchValue={state.q_entries}
                                    onSearchChange={(value) => setFilter('q_entries', value)}
                                    searchPlaceholder="Search daily entries..."
                                    isDirty={isDirty && !!state.q_entries}
                                    onClear={() => setFilter('q_entries', '')}
                                    rightSlot={`${filteredEntries.length} entries`}
                                />
                                <CompactTable data={filteredEntries} columns={dailyColumns} keyField="entry_id" />
                                {selectedEntryDetail ? (
                                    <div className="rounded-md border bg-slate-50 p-4 space-y-3">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="text-sm font-semibold text-slate-800">
                                                    Daily Entry #{selectedEntryDetail.entry_id}
                                                </div>
                                                <div className="text-xs text-slate-500">
                                                    {selectedEntryDetail.wo_item_label || WORK_ORDER_LEVEL_ENTRY_LABEL} | {selectedEntryDetail.entry_date}
                                                </div>
                                            </div>
                                            <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedEntryId(null)}>
                                                Close
                                            </Button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                                            <div className="rounded border bg-white p-3">
                                                <div className="text-xs uppercase text-slate-500">State</div>
                                                <div className="font-semibold">{selectedEntryDetail.effective_state || 'ACTIVE'}</div>
                                            </div>
                                            <div className="rounded border bg-white p-3">
                                                <div className="text-xs uppercase text-slate-500">Output</div>
                                                <div className="font-semibold">{formatQty(selectedEntryDetail.output_qty || 0)}</div>
                                            </div>
                                            <div className="rounded border bg-white p-3">
                                                <div className="text-xs uppercase text-slate-500">Weight</div>
                                                <div className="font-semibold">{formatWeight(selectedEntryDetail.output_weight_kgs || 0)} kg</div>
                                            </div>
                                            <div className="rounded border bg-white p-3">
                                                <div className="text-xs uppercase text-slate-500">Cost</div>
                                                <div className="font-semibold">{formatMoney(selectedEntryDetail.total_entry_cost || 0)}</div>
                                            </div>
                                        </div>
                                        {(selectedEntryDetail.issue_lines || []).length > 0 ? (
                                            <div>
	                                                <div className="text-sm font-semibold mb-2">Materials Used</div>
                                                <div className="space-y-2">
                                                        {(selectedEntryDetail.issue_lines || []).map((line) => (
                                                            <div key={line.issue_line_id} className="rounded border bg-white px-3 py-2 text-sm">
                                                                <div className="font-medium">{line.item_code} - {line.item_name}</div>
                                                                <div className="text-xs text-slate-500">
	                                                                    Qty {formatQty(line.qty)}{line.transaction_unit_name ? ` ${line.transaction_unit_name}` : ''} | Base {formatQty(line.base_qty ?? line.qty)} | Source {line.row_type === 'UNPLANNED' ? 'Added Manually' : 'From Estimate / Recipe'} | Cost {formatMoney(line.line_cost)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ) : null}
                                        {(selectedEntryDetail.cost_lines || []).length > 0 ? (
                                            <div>
                                                <div className="text-sm font-semibold mb-2">Cost Lines</div>
                                                <div className="space-y-2">
                                                    {(selectedEntryDetail.cost_lines || []).map((line, index) => (
                                                        <div key={`${line.cost_line_id || index}-${line.cost_type}`} className="rounded border bg-white px-3 py-2 text-sm">
                                                            <div className="font-medium">{line.cost_type}</div>
                                                            <div className="text-xs text-slate-500">
                                                                Qty {formatQty(line.qty)} | Rate {formatMoney(line.rate)} | Amount {formatMoney(line.amount)}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : null}
                                        {(selectedEntryDetail.warnings || []).length > 0 ? (
                                            <div>
                                                <div className="text-sm font-semibold mb-2">Warnings</div>
                                                <ul className="list-disc pl-5 text-sm text-amber-700 space-y-1">
                                                    {(selectedEntryDetail.warnings || []).map((warning, index) => (
                                                        <li key={`${selectedEntryDetail.entry_id}-warn-${index}`}>{warning}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </CardContent>
                    </Card>
                </div>

            </div>

            {workflowPanelOpen ? (
                <div
                    className="fixed inset-0 z-40 bg-slate-950/25"
                    onClick={() => setWorkflowPanelOpen(false)}
                />
            ) : null}

            <aside
                className={`fixed top-0 right-0 z-50 h-full w-full max-w-[440px] border-l border-slate-200 bg-slate-50 shadow-2xl transition-transform duration-300 ${
                    workflowPanelOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
            >
                <div className="h-full overflow-y-auto p-4 space-y-4">
                    <div className="flex items-center justify-between sticky top-0 bg-slate-50/95 backdrop-blur py-2 z-10">
                        <div>
                            <div className="text-sm font-semibold text-slate-800">Workflow & Status</div>
                            <div className="text-xs text-slate-500">Current status, next action, and prechecks</div>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setWorkflowPanelOpen(false)}
                        >
                            <PanelRightClose className="w-4 h-4" />
                        </Button>
                    </div>

                    <WorkflowRightPanel
                        title="Workflow & Status"
                        description="Current status, next action, and prechecks"
                    >
                        <div className="space-y-4">
                            <StatusGuidePanel
                                statusLabel={statusDefinition?.status_label || String(wo.status || `Status #${wo.status_id}`)}
                                statusMeaning={statusDefinition?.status_meaning || ''}
                                nextActions={nextActions}
                            />
                            <FlowCoachPanel entity="WORK_ORDER" entityId={wo.wo_id} />
                        </div>
                    </WorkflowRightPanel>

                    <WorkflowRightPanel
                        title="Status History"
                        description="Audit log of status changes"
                        defaultOpen={false}
                    >
                        <StatusHistoryPanel history={history} />
                    </WorkflowRightPanel>
                </div>
            </aside>

            {subPartOpen && !simpleModeEnabled && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden">
                        <div className="border-b px-6 py-4">
                            <h3 className="text-lg font-semibold">{editingSubPartId ? 'Edit Sub-part' : 'Add Sub-part'}</h3>
                            <p className="text-sm text-slate-500">Create or update a production sub-part under the selected parent manufacturing line.</p>
                        </div>
                        <div className="p-6">
                            {renderSubPartForm()}
                        </div>
                    </div>
                </div>
            )}

            {editingLineDetailsId ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
                        <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                            <div>
                                <h3 className="text-lg font-semibold">Edit Manufacturing Line Details</h3>
                                <p className="text-sm text-slate-500">
                                    Updates production specs only. Quantity, cost, postings, and history are not changed.
                                </p>
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => setEditingLineDetailsId(null)}>
                                Close
                            </Button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 p-6 md:grid-cols-2">
                            <div>
                                <label className="text-sm font-medium">Dimensions</label>
                                <input className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.dimensions || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, dimensions: event.target.value }))} />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Thickness</label>
                                <input className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.thickness || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, thickness: event.target.value }))} />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Color</label>
                                <input className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.color || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, color: event.target.value }))} />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Finish</label>
                                <input className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.finish || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, finish: event.target.value }))} />
                            </div>
                            <div>
                                <label className="text-sm font-medium">Completion Date</label>
                                <input type="date" className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.planned_completion_date || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, planned_completion_date: event.target.value }))} />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium">Remarks</label>
                                <input className="mt-1 w-full rounded border p-2" value={lineDetailsDraft.line_remarks || ''} onChange={(event) => setLineDetailsDraft((prev) => ({ ...prev, line_remarks: event.target.value }))} />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t px-6 py-4">
                            <Button type="button" variant="outline" onClick={() => setEditingLineDetailsId(null)} disabled={childActionBusyId === editingLineDetailsId}>Cancel</Button>
                            <Button type="button" onClick={handleSaveLineDetails} disabled={childActionBusyId === editingLineDetailsId}>Save Details</Button>
                        </div>
                    </div>
                </div>
            ) : null}

            {unplannedOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
                        <form onSubmit={handleAddUnplannedMaterial} className="p-6 space-y-4">
                            <h3 className="text-lg font-semibold">Add Material</h3>
                            <p className="text-sm text-slate-500">Search and add a stock material required for this work order. Notes are optional and material issue can be posted now or later.</p>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Material</label>
                                <EntityTypeaheadPicker
                                    value={unplannedItemId}
                                    options={unplannedMaterialOptions}
                                    placeholder="Search material code or name..."
                                    emptyText="No matching stock materials"
                                    menuPlacement="bottom"
                                    onChange={(value) => setUnplannedItemId(value)}
                                />
                                {selectedUnplannedItem ? (
                                    <div className="text-xs text-slate-500">
                                        Base UOM: <span className="font-medium">{selectedUnplannedItem.uom || '-'}</span>
                                        {' | '}
                                        Available: <span className="font-medium">{formatQty(selectedUnplannedItem.current_stock || 0)}</span>
                                    </div>
                                ) : null}
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Default Qty (optional)</label>
                                <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    className="w-full border rounded p-2"
                                    value={unplannedDefaultQty}
                                    onChange={e => setUnplannedDefaultQty(e.target.value)}
                                />
                                {selectedUnplannedItem ? (
                                    <div className="text-xs text-slate-500">
                                        Quantity will be treated in <span className="font-medium">{selectedUnplannedItem.uom || '-'}</span>.
                                    </div>
                                ) : null}
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">UOM</label>
                                <input
                                    type="text"
                                    className="w-full border rounded p-2 bg-slate-50 text-slate-700"
                                    value={selectedUnplannedItem?.uom || ''}
                                    placeholder="Select a material to view UOM"
                                    readOnly
                                />
                                <div className="text-xs text-slate-500">
                                    Manual material quantity is stored in this unit. Alternate issue UOM can still be selected while posting the issue.
                                </div>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button type="button" variant="ghost" onClick={() => setUnplannedOpen(false)} disabled={entryBusy}>Cancel</Button>
                                <Button type="submit" disabled={entryBusy}>{entryBusy ? 'Adding...' : 'Add Material'}</Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};
